import { Injectable, Injector, OnDestroy } from '@angular/core';

import { Observable, BehaviorSubject, Observer, of, lastValueFrom, combineLatest, take, from, forkJoin, throwError } from 'rxjs';
import { filter, catchError, mergeMap, tap, switchMap, map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { cloneDeep } from 'lodash';
import { isEqual } from 'underscore';
import * as uuid from 'uuid';

import { base64ToFile } from '@Core/utils';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { PreferencesModel } from '../../../Core/Users/models/preferences.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { RecentColorsService } from '../../../Common/Forms/components/AdvancedColorPickerPopover/service/recent-colors.service';
import { BriefingsApi } from '../../../api/briefings/briefings.api';
import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import { MonitoringRoutingService } from '../../Core/services/monitoring-routing.service';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { MonitoringRouteModel } from '../../Shared/models/monitoring-route.model';
import { BriefingListItemModel } from '../../../Common/MonitorShared/model/briefing-list-item.model';
import { BriefingsApiErrorEnum } from '../../../api/briefings/enum/briefings-api-error.enum';

// Page builder
import { BlockMoveDirectionEnum } from '../../../Common/PageBuilder/enums/direction.enum';
import { BlockModel, LayoutBlockTypesEnum } from '../../../Common/PageBuilder/models/block.model';
import { BlockTypeModel } from '../../../Common/PageBuilder/models/block-type.model';
import { TextDisplayElementModel } from '../../../Common/PageBuilder/models/text-display-element.model';
import { LayoutBlock } from '../../../Common/PageBuilder/models/layout-block.model';
import { LayoutBlockType } from '../../../Common/PageBuilder/models/layout-block-type.model';
import { LayoutDisplayElement } from '../../../Common/PageBuilder/models/layout-display-element.model';

// This module
import { AlertModel } from '../models/alert.model';
import {
    alertsBlockTypes,
    alertsBlockTypeGroups,
    recentAlertsBlockTypes,
    recentAlertsBlockTypeGroups,
    alertDateBlockType,
    alertMentionListBlockType,
    imageBlockType,
    textBlockType,
    titleBlockType,
    alertSurgeBlockType,
    surgeAlertAiCuratorBlockType,
    alertBlockTypeEnumToBlockTypeMap,
    alertDynamicMentionListBlockType,
    alertLayoutBlockTypes
} from '../models/alerts-block-types.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { UserModel } from '../../../Core/Users/models/user.model';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { FilterListProviderServiceInterface } from '../../../Common/Filter/services/filter-list-provider-service.interface';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { AbstractPageBuilderService } from '../../../Common/PageBuilder/services/abstract-page-builder.service';
import { TwigRendererService } from '../../../Common/PageBuilder/services/twig-renderer.service';
import { StylesService } from '../../../Common/Styles/services/styles.service';

import { Template } from '../../../Common/Styles/models/template.model';
import { BriefingStyleStateEnum } from '../../../Common/MonitorShared/enum/briefing-style-state.enum';
import { StyleStateEnum } from '../../../Common/PageBuilder/enums/style-state.enum';
import { GenericStyleModel } from '../../../Common/Styles/models/generic-style.model';

import { AlertBlockTypeEnum } from '../enums/alert-block-type.enum';

import { alertBlockTypeToGeneralStylesPropertyMap } from '../models/alert-global-styles-property.const';

import { defaultBriefingStyles } from '../../Shared/models/default-briefing-styles.model';
import { MentionCustomizationModel } from '../../Shared/models/mention-customization.model';
import {
    BriefingBlockTypeEnum,
    briefingBlockTypeEnumToStyleTypeEnum,
    briefingBlockTypeToGeneralStylesKey,
    isMentionBlockTypes,
    styleTypeApiEnumToBriefingBlockTypeEnum
} from '../../Briefings/enums/briefing-block-type.enum';
import { generalStyleToBriefingBlockMap, briefingBlockToGeneralStyle } from '../../Briefings/models/briefing-global-styles-property.const';
import { BriefingStatusEnum } from '../../../Common/MonitorShared/enum/briefing-status.enum';
import { BriefingStatusApiEnum } from '../../../api/briefings/enum/briefing-status-api.enum';
import { MentionSearchCriteriaModel } from '../../Shared/models/mention-search-criteria.model';
import { BriefingTypeEnum } from '../../Briefings/enums/briefing-type.enum';
import { LanguageTagEnum } from '../../../Core/Localization/enums/language-tag.enum';
import { BriefingModel } from '../../Briefings/models/briefing.model';
import { BriefingTextDisplayElementModel } from '../../../Monitor/Briefings/models/briefing-text-display-element.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { AlertCreateDialogComponent } from '../components/AlertCreateDialog/alert-create-dialog.component';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { AlertHistoryModel } from '../models/alert-history.model';
import { BriefingConfigService } from '@Common/MonitorShared/services/briefing-config.service';
import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import { AnalysisUpsellPopupComponent } from '../../Briefings/components/AnalysisUpsellPopup/analysis-upsell-popup.component';
import { AlertTypeEnum } from '../../../Common/MonitorShared/enum/alert-type.enum';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { TopicsService } from '../../../Monitor/Core/services/topics.service';
import { MentionSearchListItemModel } from '../../../Monitor/Shared/models/mention-search-list-item.model';
import { BriefingBlockTypeApiEnum } from '../../../api/briefings/enum/briefing-block-type-api.enum';
import { BriefingImageDisplayElementApiModel } from '../../../api/briefings/model/briefing-image-display-element-api.model';
import { MonitoringDateStyleModel } from '../../../Monitor/Shared/models/monitoring-date-style-model';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { BriefingTitleDisplayElementModel } from '../../../Monitor/Briefings/models/briefing-title-display-element.model';
import { BriefingImageDisplayElementModel } from '@Common/PageBuilder/models/briefing-image-display-element.model';
import { BriefingApiModel } from '@api/briefings/model/briefing-api.model';
import { CuratorBlockType } from '@Common/PageBuilder/models/curator-block-type.model';
import { promptLanguageOptions } from '@Common/Forms/data/prompt-language-options';
import { BlockDisplayTypeEnum } from '@Common/PageBuilder/enums/block-display-type.enum';
import { CuratorBlock } from '@Common/PageBuilder/models/curator-block.model';
import { CuratorDisplayElement } from '@Common/PageBuilder/models/curator-display-element.model';
import { PaddingModel } from '@Common/PageBuilder/models/padding.model';
import { BriefingHeadingLevelEnum } from '@Monitor/Briefings/enums/briefing-heading-level.enum';
import { AnalysisDisplayElementModel } from '@Monitor/Briefings/models/briefing-analysis-display-element.model';
import { BriefingCuratorBlockGenerationOptionInterface } from '@Monitor/Briefings/models/briefing-curator-block-generations.interface';
import { DynamicMentionListDisplayElementModel } from '@Monitor/Briefings/models/dynamic-mention-list-display-element.model';
import { ImageSizeEnum } from '@Monitor/Shared/enums/image-size.enum';
import { CoverageInsights } from '@Monitor/Shared/models/coverage-insights.model';
import { MonitoringBackEndDataSourceModel } from '@Monitor/Shared/models/monitoring-back-end-data-source.model';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { AiAnalysisTypes } from '@Common/Mentions/enums/ai-analysis-types.enum';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { MentionSourceApiTypesEnum } from '@api/mentions/enum/mention-source-api-types.enum';
import { BriefingChartDisplayElementModel } from '@Common/PageBuilder/models/briefing-chart-display-element.model';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import {
    ChartSetupDialogComponent
} from '../../../Common/Reports/components/ChartSetupDialog/chart-setup-dialog.component';
import { SeriesPickerFieldModel } from '../../../Common/Reports/models/series-picker-field.model';
import { DateRangeFieldModel } from '../../../Common/Reports/models/date-range-field.model';
import { SurgePeriodDateRangeFieldComponent } from '../components/SurgePeriodDateRangeField/surge-period-date-range-field.component';
import { SeriesTypeEnum } from '../../../Common/Reports/enums/series-type.enum';
import {
    ReportBlockTypeEnum,
    reportBlockTypesToNewAccentColorMapKeysMap,
    reportBlockTypesToSetupDialogAccentColorMapKeysMap,
    reportChartInfoMap
} from '../../../Common/Reports/enums/report-block-type.enum';
import { ChartSpecificTypesEnum } from '../../../Common/Reports/enums/chart-specific-types.enum';
import { MapChartColorSchemeEnum } from '../../../Common/Charts/models/map-chart.model';
import { SavedSearchType } from '../../../Common/MonitorShared/enum/saved-search-type.enum';
import { ReportSeriesModel } from '../../../Common/Reports/models/report-series.model';
import { BriefingsHelper } from '../../Briefings/helpers/briefings.helper';
import { CuratedMentionListDisplayElementModel } from '@Monitor/Briefings/models/curated-mention-list-display-element.model';
import { DisplayElementWithSearchCriteriaModel } from '@Monitor/Briefings/models/display-element-with-search-criteria.model';
import { MediaServiceAccessTypeEnum } from '@api/mediaService/enums/media-service-access-type.enum';
import { MediaResultModel, MediaServiceErrorCodesEnum } from '@api/mediaService/mediaResults.api.model';
import { MediaServiceApi } from '@api/mediaService/media-service.api';
import { dateFormats } from '../../../Core/Users/data/date-formats';
import { timeFormats } from '../../../Core/Users/data/time-formats';
import { DateDisplayElementModel } from '@Common/PageBuilder/models/date-display-element.model';


@Injectable()
export class AlertsService extends AbstractPageBuilderService implements OnDestroy, FilterListProviderServiceInterface {

    currentAlert: AlertModel = null;
    currentAlertCache: AlertModel;

    languageTag: LanguageTagEnum;
    hasCoverageInsightsEnabled: boolean = false;
    overlayContainer: Element;
    alertComponentInjector: Injector;

    private currentAlertProvider = new BehaviorSubject<AlertModel>(this.currentAlert);

    private alertsList: BriefingListItemModel[] = null;
    private alertsListProvider = new BehaviorSubject<BriefingListItemModel[]>(this.alertsList);

    private alertHistory: AlertHistoryModel = null;
    private alertHistory$ = new BehaviorSubject<AlertHistoryModel>(this.alertHistory);

    private alertSourceChanged: boolean = false;
    private alertSourceChanged$ = new BehaviorSubject<boolean>(this.alertSourceChanged);

    private refreshChartBlockData$ = new BehaviorSubject<BlockModel[]>([]);
    private chartBlockNoData$ = new BehaviorSubject<ReportSeriesModel[]>(null);

    private filterErrorSubject = new BehaviorSubject<string>('');
    filterError$ = this.filterErrorSubject.asObservable();

    private topics: MentionSearchListItemModel[];
    private userTimezonePreference: string;
    private preferences: PreferencesModel;
    private user: UserModel;

    private readonly titleBlockLimit: number = 0;
    private readonly textBlockLimit: number = 0;
    private readonly imageBlockLimit: number = 0;
    private readonly dateBlockLimit: number = 0;

    readonly CURATOR_BLOCK_MENTIONS: number = 10;
    readonly chartRowHeight: number = 125;
    readonly aiPopupWidth: number = 485;
    readonly aiPopupHeight: number = 372;
    readonly mapChartWithRegionColorsChartHeight: number = 550;

    // @TODO: Investigate whether the briefings and alerts services can both extend a base model instead of duplicating methods
    constructor(
        private briefingApi: BriefingsApi,
        private routingService: MonitoringRoutingService,
        private messageService: MessageService,
        private currentUserStateService: CurrentUserStateService,
        private coreDialogService: CoreDialogService,
        private savedSearchesApi: SavedMentionSearchesApi,
        public websocketEvents: WebsocketAwareNeptuneService,
        public stylesService: StylesService,
        public twigRenderService: TwigRendererService,
        public localizationService: LocalizationService,
        public briefingConfigService: BriefingConfigService,
        private mentionsApi: MentionsApi,
        private topicsService: TopicsService,
        private displayGroupsService: DisplayGroupsService,
        private monitorConfigService: MonitorConfigService,
        public mediaServiceApi: MediaServiceApi,
        private recentColorsService: RecentColorsService,
    ) {
        super(stylesService, twigRenderService, localizationService);

        this.setBlockTypeEnumFromStyleTypeEnumMap(styleTypeApiEnumToBriefingBlockTypeEnum);
        this.setGeneralStylesToBlocksMap(generalStyleToBriefingBlockMap);
        this.setBlockToGeneralStylePropertiesMap(briefingBlockToGeneralStyle);

        this.defaultStyles = BriefingModel.defaultBriefingStyles;

        this.setLayoutBlockTypes(alertLayoutBlockTypes);

        this.setSubscriptions();
        this.overlayContainer = document.getElementById('main-content');

        this.refreshAlertsList();
        this.setInlineAdderHeight(138);
    }


    private setSubscriptions() {
        this.subscriptions = [];
        this.subscriptions.push(
            this.routingService.getCurrentRoute().subscribe(
                routeModel => {
                    this.updateAlertFromRoute(routeModel);
                }
            ),
            this.currentUserStateService.getUser().subscribe(
                user => {
                    this.user = user;
                    this.setDefaultReplyToAndFromName();
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    const hasPreferences: boolean = preferences instanceof PreferencesModel;
                    const hasCurrentAlert: boolean = this.currentAlert instanceof AlertModel;
                    const isCurrentAlertSaved: boolean = hasCurrentAlert && this.currentAlert.isSaved;
                    const hasValidTimeZone: boolean = hasCurrentAlert && this.currentAlert.hasValidSendAtTimezone;

                    this.preferences = preferences;
                    this.userTimezonePreference = preferences.timeZone;

                    if (hasPreferences) {
                        // If the currentAlert is already loaded, and is a new (id is null) and doesn't have a timezone, default it
                        if (hasCurrentAlert && !isCurrentAlertSaved && !hasValidTimeZone) {
                            this.currentAlert.sendAt.timeZone = this.preferences.timeZone;
                            this.setCurrentAlert(this.currentAlert);

                            // Don't set the current alert as cached, create a new default alert (which will now have the timezone set)
                            // and make that the cached alert, that way if the user has already made changes other than the timezone, they
                            // won't get included in the cached copy
                            this.currentAlertCache = cloneDeep(this.getDefaultAlert(AlertTypeEnum.RECENT));
                        }

                        // If there is a current briefing, and a new mention block has been added, but it hasn't been saved yet, update the timezone
                        if (hasCurrentAlert) {
                            for (const block of this.currentAlert.blocks) {
                                if ((block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST) && !block.id && block.displayElement.styles) {
                                    block.displayElement.styles.timezone = this.preferences.timeZone;
                                }
                            }

                            this.setDefaultLanguage();
                        }
                    }
                }
            ),
            this.websocketEvents.getWebsocketMessages().pipe(filter(x => !!x)).subscribe(
                message => {
                    // Not ideal, a change in briefings refreshes both alerts and briefings
                    if (message.resourceType === ResourceType.briefings) {
                        this.refreshAlertsList();

                        // re get the briefings config to check the active alert count
                        this.briefingConfigService.refreshBriefingConfigModel();
                    }
                }
            ),
            this.hasDefaultTemplates$.subscribe(
                hasDefaults => this.setTemplatesOnBlocks(this.blocks)
            ),
            this.currentUserStateService.getUILanguageTag().subscribe(languageTag => this.languageTag = languageTag as LanguageTagEnum),
            this.topicsService.getTopics().subscribe(topics => this.topics = topics),
            this.monitorConfigService.getHasCoverageInsightsEnabled().subscribe(x => {
                this.hasCoverageInsightsEnabled = x;
                this.checkAnalysisBlockBeforeSettingBlockTypeGroups(this.currentAlert?.alertType);
            })
        );
    }

    public updateBlocks(): void {
        if (!this.currentAlert) {
            // TODO: loading here
            return;
        }

        // if the selected block no longer exists, then deselect it
        if (!this.blockExists()) {
            // we don't need to cancel changes because it's not going to get saved anyway
            this.deselectBlock(true);
        }

        // Don't call this.setBlocks because it calls setCurrentAlertBlocks and results in a infinite loop
        super.setBlocks(this.currentAlert.blocks);
        this.setBriefingBlocksActionable(this.currentAlert);
        this.setGeneralStyles(this.currentAlert.generalStyles);
        this.recentColorsService.setContext(this.currentAlert.blocks, this.currentAlert.generalStyles);
    }

    removeBlock(block: BlockModel, position?: string): void {
        const index = this.blocks.indexOf(block);
        if (index < 0 && position?.includes('.')) {
            const [blockPositionStr, columnIndexStr, blockIdxStr] = position.split('.');
            const parentBlock = this.blocks[+blockPositionStr] as LayoutBlock | CuratorBlock;
            const column = Array.isArray((parentBlock as LayoutBlock).blocks[+columnIndexStr])
                ? (parentBlock as LayoutBlock).blocks[+columnIndexStr]
                : (parentBlock as CuratorBlock).blocks;
            if (this.selectedBlock && this.selectedBlock.displayElement?.isEqual(block.displayElement)) {
                this.deselectBlock(false);
            }
            column.splice(+blockIdxStr, 1);
            this.setBlocks(this.blocks);
            return;
        }
        super.removeBlock(block, position);
    }

    duplicateBlock(block: BlockModel, position?: string): void {
        const index = this.blocks.indexOf(block);
        if (index < 0 && position?.includes('.')) {
            const [blockPositionStr, columnIndexStr, blockIdxStr] = position.split('.');
            const parentBlock = this.blocks[+blockPositionStr] as LayoutBlock | CuratorBlock;
            const column = Array.isArray((parentBlock as LayoutBlock).blocks[+columnIndexStr])
                ? (parentBlock as LayoutBlock).blocks[+columnIndexStr]
                : (parentBlock as CuratorBlock).blocks;
            column.splice(+blockIdxStr, 0, block.clone());
            this.setBlocks(this.blocks);
            return;
        }
        super.duplicateBlock(block, position);
    }

    moveBlock(block: BlockModel, direction: BlockMoveDirectionEnum, position?: string, isToLayoutBlock?: boolean): void {
        const index = this.blocks.indexOf(block);
        if (index < 0 && position?.includes('.')) {
            const [blockPositionStr, columnIndexStr, blockIdxStr] = position.split('.');
            const parentBlock = this.blocks[+blockPositionStr] as LayoutBlock | CuratorBlock;
            const column = Array.isArray((parentBlock as LayoutBlock).blocks[+columnIndexStr])
                ? (parentBlock as LayoutBlock).blocks[+columnIndexStr]
                : (parentBlock as CuratorBlock).blocks;
            const blockIdx = +blockIdxStr;

            if (isToLayoutBlock) {
                if (direction === BlockMoveDirectionEnum.up) {
                    if (blockIdx === 0) {
                        return;
                    }
                    column.splice(blockIdx, 1);
                    column.splice(blockIdx - 1, 0, block);
                } else if (direction === BlockMoveDirectionEnum.down) {
                    if (blockIdx === column.length - 1) {
                        return;
                    }
                    column.splice(blockIdx, 1);
                    column.splice(blockIdx + 1, 0, block);
                }
            } else {
                column.splice(blockIdx, 1);
                let newColumnIndex;
                switch (direction) {
                    case BlockMoveDirectionEnum.up:
                        this.blocks.splice(+blockPositionStr, 0, block);
                        break;
                    case BlockMoveDirectionEnum.down:
                        this.blocks.splice(+blockPositionStr + 1, 0, block);
                        break;
                    case BlockMoveDirectionEnum.left:
                        newColumnIndex = +columnIndexStr - 1;
                        (parentBlock as LayoutBlock).blocks[newColumnIndex].push(block);
                        break;
                    case BlockMoveDirectionEnum.right:
                        newColumnIndex = +columnIndexStr + 1;
                        (parentBlock as LayoutBlock).blocks[newColumnIndex].push(block);
                        break;
                }
            }

            this.setBlocks(this.blocks);
            return;
        }
        super.moveBlock(block, direction, position, isToLayoutBlock);
    }

    moveBlockToPosition(block: BlockModel, position: string): void {
        // Remove block from its current position
        const indexOfBlock = this.blocks.indexOf(block);
        if (indexOfBlock > -1) {
            this.blocks.splice(indexOfBlock, 1);
        } else if (this.draggingBlockPosition) {
            const [bpStr, ciStr, pipStr] = this.draggingBlockPosition.split('.');
            const parentBlock = this.blocks[+bpStr];
            const column = parentBlock instanceof LayoutBlock
                ? (parentBlock as LayoutBlock).blocks[+ciStr]
                : (parentBlock as CuratorBlock).blocks;
            column.splice(+pipStr, 1);
        } else {
            this.blocks.forEach(b => {
                if (!(b as any).blocks) {
                    return;
                }
                (b as LayoutBlock).blocks.forEach(col => {
                    const i = col.indexOf(block);
                    if (i > -1) {
                        col.splice(i, 1);
                    }
                });
            });
        }

        // Insert block at the new position
        if (isNaN(+position)) {
            const [bpStr, ciStr, pipStr] = position.split('.');
            let blockPosition = +bpStr;
            if (indexOfBlock > -1 && indexOfBlock < blockPosition) {
                blockPosition--;
            }
            const parentBlock = this.blocks[blockPosition];
            const column = parentBlock instanceof CuratorBlock
                ? (parentBlock as CuratorBlock).blocks
                : (this.blocks[blockPosition] as LayoutBlock).blocks[+ciStr];
            column.splice(+pipStr, 0, block);
        }

        this.setBlocks(this.blocks);
    }

    /**
     * Adds a block to the current list
     *
     * @param block A block object
     */
    addBlock(block: BlockModel): void {
        if (this.blockExists(block)) {
            // already in the list
            return;
        }
        this.applyGeneralStylesToBlock(block);

        if (!block.type) {
            throw new Error('Blocks must have a type');
        }

        if (block.type.key === BriefingBlockTypeEnum.TEXT) {
            (block.displayElement as BriefingTextDisplayElementModel).enableAIRefinement = true;
        }

        if ((block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST ||
            block.type.key === BriefingBlockTypeEnum.DATE) &&
            block.displayElement.styles && this.languageTag
        ) {
            if (!block.displayElement.styles.locale) {
                block.displayElement.styles.locale = this.languageTag as LanguageTagEnum;
            }
        }

        this.blocks.push(block);
        this.setBlocks(this.blocks);
    }

    addBlockOfTypeWithEvent(type: BlockTypeModel, position: string, event: Event): void {
        if ((type.key === BriefingBlockTypeEnum.ANALYSIS || type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) && !this.hasCoverageInsightsEnabled) {
            const mousePositionX = (event as PointerEvent)?.clientX || 0;
            const mousePositionY = (event as PointerEvent)?.clientY || 0;

            const ioModel = this.coreDialogService.getIoConfigBasedOnMousePosition(mousePositionX, mousePositionY, this.aiPopupWidth, this.aiPopupHeight);
            ioModel.inputs.overlayContainer = this.overlayContainer;
            this.coreDialogService.open(AnalysisUpsellPopupComponent, ioModel, this.alertComponentInjector);
            return;
        } else if (type.key === BriefingBlockTypeEnum.CHART) {
            const block = new BlockModel(null, type, new type.displayElementType());

            block.canAddChart = true;
            block.canEditChart = false;

            const generalStylesField = briefingBlockTypeToGeneralStylesKey.get(type.key as BriefingBlockTypeEnum);
            if (generalStylesField && this.generalStyles[generalStylesField]) {
                const defaultReference: string = this.generalStyles[generalStylesField].getDefaultReference();
                block.type.twig = this.getDefaultTemplateByBlockType(type.key, defaultReference);
                this.applyGeneralStylesToBlock(block);
            }

            block.displayElement.displayType = BlockDisplayTypeEnum.TWIG;

            if (!isNaN(+position) || !position) {
                const positionToAdd = !position ? null : +position;
                if ((!!positionToAdd || positionToAdd === 0) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
                    this.blocks.splice(positionToAdd, 0, block);
                } else {
                    this.blocks.push(block);
                }
            } else {
                if (position) {
                    const [blockPosition, columnIndex, positionInColumn] = position.split('.');
                    const layoutBlock = this.blocks[+blockPosition] as LayoutBlock;
                    const column = layoutBlock.blocks[+columnIndex];
                    const positionToAdd = +positionInColumn;
                    if ((!!positionToAdd || positionToAdd === 0) && positionToAdd >= 0 && positionToAdd < column.length) {
                        column.splice(positionToAdd, 0, block);
                    } else {
                        column.push(block);
                    }
                }
            }

            this.selectBlock(block);
            this.setBlocks(this.blocks);

            this.setFocusedBlockInfo({
                blockPosition: position,
                focusedBlockButtonType: null
            });
        } else {
            this.addBlockOfType(type, position);
        }
    }

    /**
     * Adds a block to the current list
     *
     * @param type A block type
     */
    addBlockOfType(type: BlockTypeModel, position?: string): void {
        const positionToAdd = position ? +position : null;
        let block;

        if (type.key === BriefingBlockTypeEnum.ONE_COLUMN_LAYOUT ||
            type.key === BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT ||
            type.key === BriefingBlockTypeEnum.THREE_COLUMN_LAYOUT) {
            block = new LayoutBlock(null, type as LayoutBlockType, new type.displayElementType() as LayoutDisplayElement);
        } else if (type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
            block = this.createCuratorBlock(type as CuratorBlockType, {});
        } else {
            block = new BlockModel(null, type, new type.displayElementType());
        }

        if (block.type.key === BriefingBlockTypeEnum.TEXT) {
            (block.displayElement as BriefingTextDisplayElementModel).enableAIRefinement = true;
        }

        if ((block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST ||
            block.type.key === BriefingBlockTypeEnum.ALERT_MENTIONS ||
            block.type.key === BriefingBlockTypeEnum.DATE) &&
            block.displayElement.styles && this.languageTag
        ) {
            if (!block.displayElement.styles.locale) {
                block.displayElement.styles.locale = this.languageTag as LanguageTagEnum;
            }
        }

        if (type.key === BriefingBlockTypeEnum.DATE) {
            this.localizeDateBlock(block);
        }

        if (type.key === BriefingBlockTypeEnum.CHART) {
            block.canAddChart = true;
            block.canEditChart = false;
            const generalStylesField = briefingBlockTypeToGeneralStylesKey.get(type.key as BriefingBlockTypeEnum);
            if (generalStylesField && this.generalStyles[generalStylesField]) {
                const defaultReference: string = this.generalStyles[generalStylesField].getDefaultReference();
                block.type.twig = this.getDefaultTemplateByBlockType(type.key, defaultReference);
                this.applyGeneralStylesToBlock(block);
            }
        }

        if (type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
            this.setTemplatesOnBlocks((block as CuratorBlock).blocks);
        }

        if (type.key === BriefingBlockTypeEnum.ANALYSIS) {
            const analysisDisplayElement = (block.displayElement as AnalysisDisplayElementModel);
            analysisDisplayElement.enableAIRefinement = true;
            analysisDisplayElement.generateOnSend = true;
            analysisDisplayElement.analysisField = new CoverageInsights();
            analysisDisplayElement.backendDataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.coverageSurge);
            analysisDisplayElement.sourceType = MentionSourceApiTypesEnum.COVERAGE_SURGE;
            analysisDisplayElement.analysisField.aiAnalysisType = AiAnalysisTypes.COVERAGE_SURGE_INSIGHT;
            analysisDisplayElement.analysisField.includeThemes = true;
            analysisDisplayElement.analysisField.includeStrategy = true;
            analysisDisplayElement.analysisField.includeSentiment = true;
            analysisDisplayElement.analysisField.keyTakeawayHeading = null;
            analysisDisplayElement.analysisField.numberKeyMessages = 1;
            if (this.cachedSelectedDate) {
                if (analysisDisplayElement?.backendDataSource?.combinedSearchCriteria?.query) {
                    analysisDisplayElement.backendDataSource.combinedSearchCriteria.query.dateQuery = this.cachedSelectedDate;
                }
                if (analysisDisplayElement?.backendDataSource?.combinedSearchCriteria?.filterQuery) {
                    analysisDisplayElement.backendDataSource.combinedSearchCriteria.filterQuery.dateQuery = this.cachedSelectedDate;
                }
                if (analysisDisplayElement?.backendDataSource?.filterSearchCriteria?.query) {
                    analysisDisplayElement.backendDataSource.filterSearchCriteria.query.dateQuery = this.cachedSelectedDate;
                }
            }
            block.type.twig = this.getDefaultTemplateByBlockType(
                block.type.key,
                block.displayElement.styles.getDefaultReference()
            );
        }


        if (type.key !== BriefingBlockTypeEnum.ONE_COLUMN_LAYOUT &&
            type.key !== BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT &&
            type.key !== BriefingBlockTypeEnum.THREE_COLUMN_LAYOUT &&
            type.key !== BriefingBlockTypeEnum.COVERAGE_CURATOR &&
            type.key !== BriefingBlockTypeEnum.CHART &&
            type.key !== BriefingBlockTypeEnum.ANALYSIS) {
            const generalStylesField = briefingBlockTypeToGeneralStylesKey.get(type.key as BriefingBlockTypeEnum);
            if (generalStylesField && this.generalStyles[generalStylesField]) {
                const defaultReference: string = this.generalStyles[generalStylesField].getDefaultReference();
                block.type.twig = this.getDefaultTemplateByBlockType(type.key, defaultReference);
                this.applyGeneralStylesToBlock(block);
            }
        }

        if (!isNaN(+position) || !position) {
            if ((!!positionToAdd || positionToAdd === 0) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
                this.blocks.splice(positionToAdd, 0, block);
            } else {
                this.blocks.push(block);
            }
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const parentBlock = this.blocks[+blockPosition];

            let column: BlockModel[];
            if (!isNaN(+columnIndex)) {
                column = (parentBlock as LayoutBlock).blocks[+columnIndex];
            } else {
                const curatorBlock = parentBlock as CuratorBlock;
                column = curatorBlock.blocks;

                block.displayElement.canAutomaticallyGenerated = true;
                block.displayElement.automaticallyGenerated = true;

                if (block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                    const displayElement = block.displayElement as DynamicMentionListDisplayElementModel | AnalysisDisplayElementModel;
                    displayElement.backendDataSource = curatorBlock.displayElement.backendDataSource;
                    displayElement.searchCriteriaId = curatorBlock.displayElement.searchCriteriaId;
                    displayElement.sourceType = curatorBlock.displayElement.sourceType;
                    displayElement.filterMentionSearchModel = curatorBlock.displayElement.filterMentionSearchModel;
                }
            }

            const positionInColumnToAdd = +positionInColumn;
            if ((!!positionInColumnToAdd || positionInColumnToAdd === 0) && positionInColumnToAdd >= 0 && positionInColumnToAdd < column.length) {
                column.splice(positionInColumnToAdd, 0, block);
            } else {
                column.push(block);
            }
        }

        this.setBlocks(this.blocks);
    }

    updateChart(block: BlockModel): void {
        if (!block) {
            return;
        }

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const chartBlock = (block.displayElement as BriefingChartDisplayElementModel)?.chartBlock;
        if (chartBlock?.displayElement && this.currentAlert?.alertType === AlertTypeEnum.SURGE) {
            (chartBlock.displayElement as any).useSearchDateFilter =
                (block.displayElement as BriefingChartDisplayElementModel).useSearchDateFilter;
        }
        const oldChartBlock = chartBlock?.clone();
        const oldBlockType = oldChartBlock?.type?.key;
        const oldChartDisplayElement = oldChartBlock?.displayElement as ChartDisplayElementModel;

        const ioModel = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                selectedBlock: chartBlock,
                hideDataConnectorCharts: true,
                hideMentionCharts: true,
                hideMetadataContainer: true,
                hidePreviewChartTooltip: true,
                globalDateQuery: null,
                displayEntityProfile: false,
                displaySourceAsLink: false,
                accentColor: this.currentAlert?.generalStyles?.['accentColor'],
                // For surge alerts, override chart fields each time the user picks a chart type:
                // - restrict source types to those compatible with surge alerts
                // - swap in SurgePeriodDateRangeFieldComponent so the date picker includes the "Surge Period" option
                overrideBlockTypeFields: this.currentAlert?.alertType === AlertTypeEnum.SURGE
                    ? (type) => {
                        const surgeSeriesTypes = [
                            SeriesTypeEnum.topic,
                            SeriesTypeEnum.subscription,
                            SeriesTypeEnum.socialTopic,
                            SeriesTypeEnum.theme,
                        ];
                        const seriesPickerIndex = type.fields.findIndex(f => f instanceof SeriesPickerFieldModel);
                        if (seriesPickerIndex !== -1) {
                            const original = type.fields[seriesPickerIndex] as SeriesPickerFieldModel;
                            type.fields[seriesPickerIndex] = new SeriesPickerFieldModel(
                                original.fieldComponent,
                                original.property,
                                original.label,
                                original.description,
                                original.multiSeries,
                                original.maxNumberOfSeries,
                                surgeSeriesTypes,
                                original.filters,
                                original.tooltipMessage,
                                original.showColorSwatch,
                                original.isBarChart,
                                original.required,
                                original.headingId,
                                original.appearCondition
                            );
                        }
                        const dateRangeIndex = type.fields.findIndex(f => f instanceof DateRangeFieldModel);
                        if (dateRangeIndex !== -1) {
                            const original = type.fields[dateRangeIndex] as DateRangeFieldModel;
                            type.fields[dateRangeIndex] = new DateRangeFieldModel(
                                SurgePeriodDateRangeFieldComponent,
                                original.property,
                                original.label,
                                original.description,
                                original.allowAllOption,
                                original.appearCondition
                            );
                        }
                    }
                    : null
            },
            outputs: {
                dialogClose: (output) => {
                    if (output?.block) {
                        const briefingChartDisplayElement = block.displayElement as BriefingChartDisplayElementModel;
                        briefingChartDisplayElement.chartBlock = output.block;
                        if (this.currentAlert?.alertType === AlertTypeEnum.SURGE) {
                            briefingChartDisplayElement.useSearchDateFilter =
                                (output.block.displayElement as any).useSearchDateFilter === false ? false : true;
                        }
                        block.canAddChart = false;
                        block.canEditChart = true;
                        block.canAnnotate = output.block.canAnnotate;

                        if (output.block.type.key !== oldBlockType) {
                            const rows = reportChartInfoMap.get(
                                output.block.type.key as ReportBlockTypeEnum
                            )?.dimensions?.height;
                            if (rows) {
                                briefingChartDisplayElement.chart.heightPx = this.chartRowHeight * rows;
                            }

                            const isMapChartWithRegionColors =
                                output.block.type.key === ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME
                                && output.block.displayElement?.chartSpecificType === ChartSpecificTypesEnum.MAP
                                && output.block.displayElement?.mapChartColorScheme === MapChartColorSchemeEnum.REGION_COLORS;
                            if (isMapChartWithRegionColors) {
                                briefingChartDisplayElement.chart.heightPx = this.mapChartWithRegionColorsChartHeight;
                            }

                            if (output.block.type.key === ReportBlockTypeEnum.COVERAGE_METRIC) {
                                briefingChartDisplayElement.chart.heightPx = 200;
                            }
                        }

                        const chartDisplayElement = briefingChartDisplayElement.chartBlock
                            .displayElement as ChartDisplayElementModel;

                        const trackedColorMapKeys = reportBlockTypesToNewAccentColorMapKeysMap.get(output.block.type.key)
                            || reportBlockTypesToSetupDialogAccentColorMapKeysMap.get(output.block.type.key);
                        if (trackedColorMapKeys) {
                            const oldColors = oldChartBlock && oldChartBlock.type.key === output.block.type.key ?
                                trackedColorMapKeys.map(x => oldChartDisplayElement.styles.colorMap[x])
                                : trackedColorMapKeys.map(x => this.currentAlert?.generalStyles?.['accentColor']);
                            const newColors = trackedColorMapKeys.map(
                                x => (output.block.displayElement as ChartDisplayElementModel).styles.colorMap[x]
                            );
                            chartDisplayElement.trackedColorChanged = chartDisplayElement.trackedColorChanged
                                || oldColors.some((x, i) => x !== newColors[i]);
                        }

                        let refreshBlockData = false;
                        chartDisplayElement.series?.forEach((series, i) => {
                            const mentionFilter = series.mentions?.filters;
                            series.cacheResult = !briefingChartDisplayElement.chart.generateOnSend;

                            if (mentionFilter) {
                                mentionFilter.type = SavedSearchType.BRIEFING;
                                mentionFilter.searchCriteria = series.cibFilterSearchCriteriaModel();
                            }

                            if (oldChartDisplayElement?.series?.[i]?.mentions?.filters) {
                                if (!mentionFilter?.searchCriteria?.query.equals(
                                    oldChartDisplayElement.series?.[i]?.mentions?.filters?.searchCriteria?.query)
                                ) {
                                    refreshBlockData = true;
                                }
                            }

                            if (i < briefingChartDisplayElement.mentionSearches.length && mentionFilter) {
                                const id = briefingChartDisplayElement.mentionSearches[i].id;
                                const name = briefingChartDisplayElement.mentionSearches[i].name;
                                briefingChartDisplayElement.mentionSearches[i] = mentionFilter;
                                briefingChartDisplayElement.mentionSearches[i].id = id;
                                briefingChartDisplayElement.mentionSearches[i].name = name;
                            } else if (mentionFilter) {
                                delete mentionFilter.id;
                                delete mentionFilter.name;
                                briefingChartDisplayElement.mentionSearches.push(mentionFilter);
                            }

                            const neoFacets = series.mentions?.neoFacets;
                            if (neoFacets) {
                                briefingChartDisplayElement.mentionSearches[i].searchCriteria.neoFacets = neoFacets;
                            }

                            if (!oldChartDisplayElement ||
                                series.label !== oldChartDisplayElement.series[i]?.label ||
                                series.styles.geographyScale !== oldChartDisplayElement.series[i]?.styles.geographyScale ||
                                series.styles.networkSelection !== oldChartDisplayElement.series[i]?.styles.networkSelection ||
                                series.prominenceTrendGroup !== oldChartDisplayElement.series[i]?.prominenceTrendGroup) {
                                refreshBlockData = true;
                            }
                        });

                        if (this.needsChartDataRefresh(chartDisplayElement, oldChartDisplayElement)) {
                            refreshBlockData = true;
                        }

                        this.setBlocks(this.blocks);
                        this.notifyBlockDisplayElementChanged(block);
                        if (refreshBlockData) {
                            this.refreshBlockData(block);
                        } else {
                            const oldSeries = (chartBlock.displayElement as ChartDisplayElementModel).series;
                            chartDisplayElement.series?.forEach((newSeriesItem, i) => {
                                const oldSeriesItem = oldSeries?.[i];
                                if (oldSeriesItem?.dataProvider.value !== null) {
                                    newSeriesItem.dataProvider.next(oldSeriesItem.dataProvider.value);
                                }
                                if (oldSeriesItem?.previousPeriodDataProvider?.value !== null) {
                                    newSeriesItem.previousPeriodDataProvider?.next(
                                        oldSeriesItem.previousPeriodDataProvider.value
                                    );
                                }
                            });
                            requestAnimationFrame(() => {
                                this.blockDisplayElementChanged$.next({block: block});
                            });
                        }

                    }
                }
            }
        };

        this.coreDialogService.open(ChartSetupDialogComponent, ioModel, this.alertComponentInjector);
    }

    private needsChartDataRefresh(
        newChartDisplayElement: ChartDisplayElementModel,
        oldChartDisplayElement: ChartDisplayElementModel
    ): boolean {
        if (!oldChartDisplayElement) {
            return true;
        }
        return newChartDisplayElement.dateRangeLabel !== oldChartDisplayElement.dateRangeLabel ||
            newChartDisplayElement.metric !== oldChartDisplayElement.metric ||
            !isEqual(newChartDisplayElement.metrics, oldChartDisplayElement.metrics) ||
            newChartDisplayElement.chartHeaderSettings.previousPeriod !== oldChartDisplayElement.chartHeaderSettings.previousPeriod ||
            newChartDisplayElement.termType !== oldChartDisplayElement.termType ||
            !isEqual(newChartDisplayElement.sentiments, oldChartDisplayElement.sentiments) ||
            newChartDisplayElement.numOfItems !== oldChartDisplayElement.numOfItems;
    }

    refreshBlockData(block: BlockModel): void {
        const b = BriefingsHelper.getChartBlocks(this.currentAlert).find(x => x.equals(block));
        this.refreshChartBlockData$.next([b]);
    }

    refreshChartBlockData(): Observable<BlockModel[]> {
        return this.refreshChartBlockData$;
    }

    setBlockNoData(series: ReportSeriesModel[]): void {
        this.chartBlockNoData$.next(series);
    }

    getBlockNoData(): Observable<ReportSeriesModel[]> {
        return this.chartBlockNoData$;
    }

    createCuratorBlock(
        type: CuratorBlockType,
        options: BriefingCuratorBlockGenerationOptionInterface,
        briefingStyleState: BriefingStyleStateEnum = BriefingStyleStateEnum.BRIEFING_1
    ): CuratorBlock {
        const block = new CuratorBlock(null, type as CuratorBlockType, options.displayElement || new type.displayElementType() as CuratorDisplayElement);
        const curatorDisplayElement = block.displayElement as CuratorDisplayElement;
        const dataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.coverageSurge);
        dataSource.filterSearchCriteria.query.dateQuery = DateQueryModel.getLast24HoursQuery();
        const sourceBlockReferenceId =  uuid.v4();
        let preferenceLanguageTag = this.languageTag.toString();
        if (preferenceLanguageTag === 'fr-FR') {
            preferenceLanguageTag = 'fr';
        }
        curatorDisplayElement.backendDataSource = dataSource;
        curatorDisplayElement.bulkAnalysisMessage = new CoverageInsights();
        curatorDisplayElement.bulkAnalysisMessage.aiAnalysisType = AiAnalysisTypes.COLLECTION_TOP_MENTIONS;
        curatorDisplayElement.bulkAnalysisMessage.selectedLanguageOption =
            curatorDisplayElement.bulkAnalysisMessage.languageOptions.find(option => option.value === 'en-US');
        if (this.currentAlert?.alertType === AlertTypeEnum.SURGE) {
            curatorDisplayElement.bulkAnalysisMessage.focus = 'Coverage Surge Event Top Narratives';
            curatorDisplayElement.generateOnSend = true;
        }

        let subBlock;
        let subBlockDisplayElement;

        // title block
        subBlockDisplayElement = new BriefingTitleDisplayElementModel(
            this.localizationService.transform('Alerts.surgeInsights.label'),
            this.getDefaultStyle(AlertBlockTypeEnum.TITLE, briefingStyleState)
        );
        (subBlockDisplayElement as BriefingTitleDisplayElementModel).headingLevel = BriefingHeadingLevelEnum.HEADING_3;
        subBlockDisplayElement.styles.padding = new PaddingModel(0, true, 20, 0, 10, 0);
        subBlockDisplayElement.styles.headingTwo.padding = new PaddingModel(null, true, 20, 0, 10, 0);
        subBlockDisplayElement.styles.headingThree.padding = new PaddingModel(null, true, 20, 0, 10, 0);
        subBlockDisplayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        subBlockDisplayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        subBlockDisplayElement.canAutomaticallyGenerated = true;
        subBlockDisplayElement.automaticallyGenerated = false;

        subBlock = new BlockModel(
            null,
            alertBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.TITLE),
            subBlockDisplayElement
        );
        block.blocks.push(subBlock);

        // analysis block
        subBlockDisplayElement = new AnalysisDisplayElementModel();
        subBlockDisplayElement.generateOnSend = true;
        subBlockDisplayElement.canAutomaticallyGenerated = true;
        subBlockDisplayElement.automaticallyGenerated = true;
        subBlockDisplayElement.analysisField = new CoverageInsights();
        subBlockDisplayElement.analysisField.aiAnalysisType = AiAnalysisTypes.COVERAGE_SURGE_INSIGHT;
        subBlockDisplayElement.analysisField.includeThemes = true;
        subBlockDisplayElement.analysisField.includeStrategy = true;
        subBlockDisplayElement.analysisField.includeSentiment = true;
        subBlockDisplayElement.analysisField.keyTakeawayHeading = null;
        subBlockDisplayElement.analysisField.numberKeyMessages = 1;
        subBlockDisplayElement.analysisField.selectedLanguageOption = promptLanguageOptions.find(option => preferenceLanguageTag === option.value);
        subBlockDisplayElement.backendDataSource = curatorDisplayElement.backendDataSource;
        subBlockDisplayElement.sourceType = curatorDisplayElement.sourceType;
        subBlockDisplayElement.backendDataSource.referenceId = sourceBlockReferenceId;
        subBlockDisplayElement.searchCriteriaId = curatorDisplayElement.searchCriteriaId;
        subBlockDisplayElement.filterMentionSearchModel = curatorDisplayElement.filterMentionSearchModel;
        subBlock = new BlockModel(
            null,
            alertBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.ANALYSIS),
            subBlockDisplayElement
        );
        block.blocks.push(subBlock);

        // Surge Mention Examples title block
        const mentionExamplesTitleDisplay = new BriefingTitleDisplayElementModel(
            this.localizationService.transform('Alerts.surgeMentionExamples.label'),
            this.getDefaultStyle(AlertBlockTypeEnum.TITLE, briefingStyleState)
        );
        (mentionExamplesTitleDisplay as BriefingTitleDisplayElementModel).headingLevel = BriefingHeadingLevelEnum.HEADING_3;
        mentionExamplesTitleDisplay.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        mentionExamplesTitleDisplay.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        mentionExamplesTitleDisplay.canAutomaticallyGenerated = true;
        mentionExamplesTitleDisplay.automaticallyGenerated = false;
        block.blocks.push(new BlockModel(
            undefined,
            alertBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.TITLE),
            mentionExamplesTitleDisplay
        ));

        // Coverage Block
        subBlock = this.createDynamicBlock(dataSource, this.CURATOR_BLOCK_MENTIONS, '', sourceBlockReferenceId);
        subBlock.displayElement.styles.accentColor = options.accentColor;
        (subBlock.displayElement as DynamicMentionListDisplayElementModel).title = 'Curator Mentions';
        (subBlock.displayElement as DynamicMentionListDisplayElementModel).styles.showSectionHeading = false;
        (subBlock.displayElement as DynamicMentionListDisplayElementModel).styles.image = true;
        (subBlock.displayElement as DynamicMentionListDisplayElementModel).styles.imageSize = ImageSizeEnum.fullWidth;
        subBlock.displayElement.canAutomaticallyGenerated = true;
        subBlock.displayElement.automaticallyGenerated = true;
        subBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        subBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        subBlock.displayElement.filterMentionSearchModel = curatorDisplayElement.filterMentionSearchModel;

        block.blocks.push(subBlock);

        return block;
    }
    /**
     * Creates a new dynamic block
     */
    public createDynamicBlock(dataSource: MonitoringBackEndDataSourceModel, maxMentions: number, title: string, blockReferenceId): BlockModel {
        const dynamicBlock = new BlockModel(
            null,
            alertDynamicMentionListBlockType,
            new (alertDynamicMentionListBlockType.displayElementType)()
        );

        const dynamicDisplay = (dynamicBlock.displayElement as DynamicMentionListDisplayElementModel);
        if (this.currentAlert?.generalStyles?.mention) {
            dynamicDisplay.styles = new MentionCustomizationModel(this.currentAlert.generalStyles.mention.clone());
        } else {
            dynamicDisplay.styles = this.getDefaultStyle(AlertBlockTypeEnum.AUTOMATIC_MENTION_LIST);
        }
        dynamicDisplay.backendDataSource = dataSource;
        dynamicDisplay.pageSize = maxMentions;
        dynamicDisplay.title = title;
        dynamicDisplay.onUpdate();
        dynamicBlock.type.twig = this.getDefaultTemplateByBlockType(
            AlertBlockTypeEnum.AUTOMATIC_MENTION_LIST,
            BriefingStyleStateEnum.BRIEFING_1
        );
        dynamicDisplay.blockReferenceId = blockReferenceId;
        if (this.languageTag) {
            dynamicDisplay.styles.locale = this.languageTag;
        }

        return dynamicBlock;
    }
    /**
         * Gets all of the mention blocks on the current page (not analysis blocks)
         */
    getAllMentionBlocks(): BlockModel[] {
        const mentionBlocks = [];
        this.currentAlert?.blocks.forEach(block => {
            if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        // TODO: REMOVE saved source check
                        if (isMentionBlockTypes(x.type) &&
                                (x.displayElement as DynamicMentionListDisplayElementModel)?.blockReferenceId &&
                                (
                                    (x.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isSavedSource() ||
                                    (x.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isUnsavedSource() ||
                                    (x.displayElement as CuratedMentionListDisplayElementModel)?.mentionIds?.length
                                )
                        ) {
                            mentionBlocks.push(x);
                        }
                    });
                });
            } else if ((block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR)) {
                (block as CuratorBlock).blocks.forEach(item => {
                    if (isMentionBlockTypes(item.type) &&
                            (item.displayElement as DynamicMentionListDisplayElementModel)?.blockReferenceId &&
                            (
                                (item.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isSavedSource() ||
                                (item.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isUnsavedSource() ||
                                (item.displayElement as CuratedMentionListDisplayElementModel)?.mentionIds?.length
                            )
                    ) {
                        mentionBlocks.push(item);
                    }
                });
            } else {
                // TODO: REMOVE saved source check
                if (isMentionBlockTypes(block.type) &&
                        (block.displayElement as DynamicMentionListDisplayElementModel)?.blockReferenceId &&
                        (
                            (block.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isSavedSource() ||
                            (block.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isUnsavedSource() ||
                            (block.displayElement as CuratedMentionListDisplayElementModel)?.mentionIds?.length
                        )
                ) {
                    mentionBlocks.push(block);
                }
            }
        });

        return mentionBlocks;
    }

    /**
     * Sets briefing blocks actionable based on status
     *
     * @param currentAlert - the current briefing
     */
    setBriefingBlocksActionable(currentAlert: AlertModel) {
        if ((currentAlert.status === BriefingStatusEnum.SENT || currentAlert.status === BriefingStatusEnum.SCHEDULED)) {
            this.setBlocksActionable(false);
            return;
        }

        if (currentAlert?.alertType === AlertTypeEnum.SURGE) {
            this.blocks.forEach(block => {
                block.actionable = block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR;
            });
        }
        this.setBlocksActionable(true);
    }

    /**
     * Updates the list of blocks
     */
    setBlocks(blocks: BlockModel[]): void {
        super.setBlocks(blocks);
        this.setCurrentAlertBlocks(this.blocks);
        // I don't think this is needed..
        // seems to be overkill since we are calling these functions in setCurrentAlertBlocks
        // this.setTemplatesOnBlocks(this.blocks);
        // this.setCurrentAlert(this.currentAlert);
    }

    /**
     * Caches a mapping of template type to template
     */
    cacheTemplates(templates: Template[]) {
        templates.forEach(template => {
            if (template.isDefault && template.defaultReference) {
                this.defaultTemplates.set(
                    template.type.toUpperCase() + template.defaultReference.toUpperCase(),
                    template.template
                );
            }
        });

        // HACK: The template service doesn't have a DEFAULT template for images yet, this can be removed when it does
        if (this.defaultTemplates.has(imageBlockType.key.toUpperCase() + BriefingStyleStateEnum.BRIEFING_1.toUpperCase())) {
            this.defaultTemplates.set(
                imageBlockType.key.toUpperCase() + StyleStateEnum.DEFAULT,
                this.defaultTemplates.get(imageBlockType.key.toUpperCase() + BriefingStyleStateEnum.BRIEFING_1.toUpperCase())
            );
        }

        this.hasDefaultTemplates$.next(true);
    }

    /**
     * Updates the status of a block type that is limited by count
     */
    private updateLimitedBlockTypeStatus(blockType: BlockTypeModel, limit: number, count: number = 0) {
        if (!blockType) {
            return;
        }
        if (limit) {
            blockType.canAdd = count < limit;
            blockType.remainingCount = Math.max(limit - count, 0);
        }
    }

    /**
     * Updates the block type statuses of all of the block types
     *
     */
    areLayoutBlocksAllowed(): boolean {
        return true;
    }

    protected updateBlockTypeStatuses(): void {
        const blockCounts = {};
        for (const block of this.blocks) {
            const key = block.type.key;
            if (!blockCounts[key]) {
                blockCounts[key] = 0;
            }
            blockCounts[key]++;
        }

        this.updateLimitedBlockTypeStatus(
            titleBlockType,
            this.titleBlockLimit,
            blockCounts[titleBlockType.key]
        );
        this.updateLimitedBlockTypeStatus(
            textBlockType,
            this.textBlockLimit,
            blockCounts[textBlockType.key]
        );
        this.updateLimitedBlockTypeStatus(
            imageBlockType,
            this.imageBlockLimit,
            blockCounts[imageBlockType.key]
        );
        this.updateLimitedBlockTypeStatus(
            alertDateBlockType,
            this.dateBlockLimit,
            blockCounts[alertDateBlockType.key]
        );

        this.blockTypesProvider.next(this.blockTypes);
    }

    /**
     * Sets the default twig template on each block, which in alerts comes only from the block type
     */
    setTemplatesOnBlocks(blocks: BlockModel[]): void {
        for (const block of blocks) {
            // Expert template case
            if (block?.displayElement?.styles?.template?.template) {
                block.type.twig = block.displayElement.styles.template.template;
            } else {
                if (!block?.type?.key || block.type.key === AlertBlockTypeEnum.ALERT_MENTIONS) {
                    block.type.twig = this.getDefaultTemplateByBlockType(
                        BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST,
                        block.displayElement.styles.getDefaultReference()
                    );
                    continue;
                }

                block.type.twig = this.getDefaultTemplateByBlockType(
                    block.type.key,
                    block.displayElement.styles.getDefaultReference()
                );
            }
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks?.forEach(column => {
                    this.setTemplatesOnBlocks(column);
                });
            }

            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                this.setTemplatesOnBlocks((block as CuratorBlock).blocks);
            }

            if ((block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST ||
                block.type.key === BriefingBlockTypeEnum.ALERT_MENTIONS ||
                block.type.key === BriefingBlockTypeEnum.DATE) &&
                block.displayElement.styles && this.languageTag
            ) {
                if (!block.displayElement.styles.locale) {
                    block.displayElement.styles.locale = this.languageTag as LanguageTagEnum;
                }
            }
        }
    }
    /**
     * Returns the default template for the provided block type
     */
    getDefaultTemplateByBlockType(blockType: string, defaultReference: string): string {
        // Backwards compatibility and support for images
        if (!defaultReference || defaultReference === BriefingStyleStateEnum.DEFAULT) {
            defaultReference = BriefingStyleStateEnum.BRIEFING_1;
        }
        return this.defaultTemplates.get(briefingBlockTypeEnumToStyleTypeEnum.get(blockType as BriefingBlockTypeEnum) + defaultReference?.toUpperCase());
    }

    blockEditingStateChanged(blockModel: BlockModel, isEditing: boolean, blockPosition: string): void {
        blockModel.canMove = !isEditing;
        blockModel.draggable = !isEditing;
        this.notifyBlockDisplayElementChanged(blockModel);
        this.updateMoveability();
    }

    protected updateMoveability(): void {
        if (this.blocks.length === 0) {
            return;
        }
        for (let i = 0, l = this.blocks.length; i < l; i++) {
            this.blocks[i].canMoveUp = true;
            this.blocks[i].canMoveUpIntoLayout = false;
            this.blocks[i].canMoveUpIntoCurator = false;
            this.blocks[i].canMoveDown = true;
            this.blocks[i].canMoveDownIntoLayout = false;
            this.blocks[i].canMoveDownIntoCurator = false;
            this.blocks[i].canMoveLeft = false;
            this.blocks[i].canMoveRight = false;
            if (this.blocks[i].isLayoutBlockType()) {
                this.updateLayoutBlockMoveability(this.blocks[i] as LayoutBlock);
            } else if (this.blocks[i].type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                this.updateCoverageCuratorBlockMoveability(this.blocks[i] as CuratorBlock);
            } else {
                if (this.blocks[i - 1]?.isLayoutBlockType()) {
                    this.blocks[i].canMoveUpIntoLayout = true;
                }
                if (this.blocks[i + 1]?.isLayoutBlockType()) {
                    this.blocks[i].canMoveDownIntoLayout = true;
                }
                if (this.blocks[i - 1]?.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                    this.blocks[i].canMoveUpIntoCurator = true;
                }
                if (this.blocks[i + 1]?.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                    this.blocks[i].canMoveDownIntoCurator = true;
                }
            }
        }
        this.blocks[0].canMoveUp = false;
        this.blocks[this.blocks.length - 1].canMoveDown = false;
    }

    private updateLayoutBlockMoveability(layoutBlock: LayoutBlock): void {
        const columnLength = layoutBlock.blocks.length;
        layoutBlock.blocks.forEach((column, columnIndex) => {
            const blockLength = column.length;
            column.forEach((block, blockIndex) => {
                block.canMoveUp = blockIndex !== 0;
                block.canMoveDown = blockIndex !== blockLength - 1;
                block.canMoveLeft = columnIndex !== 0;
                block.canMoveRight = columnIndex !== columnLength - 1;
            });
        });
    }

    private updateCoverageCuratorBlockMoveability(curatorBlock: CuratorBlock): void {
        const blockLength = curatorBlock.blocks.length;
        curatorBlock.blocks.forEach((block, blockIndex) => {
            block.canMoveUp = blockIndex !== 0;
            block.canMoveDown = blockIndex !== blockLength - 1;
        });
    }

    /**
     * Handles change events to generalStyles
     */
    generalStylesBlockUpdated(blockType: AlertBlockTypeEnum, blockStyles: GenericStyleModel): void {
        this.blocks.forEach(block => {
            if (blockType === block.type.key && block.displayElement.isBoundToGlobalStyles) {
                this.setPropertyValue(block.displayElement, 'styles', blockStyles.clone(block.displayElement.styles as GenericStyleModel));
            }
        });

        if (this.selectedBlock) {
            this.selectedBlockUpdated();
        }
        this.setTemplatesOnBlocks(this.currentAlert?.blocks);
        this.generalStylesProvider.next(this.generalStyles);
        this.setCurrentAlert(this.currentAlert);
    }

    getDefaultStyle(blockTypeEnum: AlertBlockTypeEnum, defaultReference?: BriefingStyleStateEnum) {
        if (defaultReference === BriefingStyleStateEnum.DEFAULT || !defaultReference) {
            defaultReference = BriefingStyleStateEnum.BRIEFING_1;
        }

        const generalStylesProperty = alertBlockTypeToGeneralStylesPropertyMap.get(blockTypeEnum);
        const defaultValue = defaultBriefingStyles[defaultReference][generalStylesProperty].clone();
        defaultValue.displayStyleState = defaultValue.styleState;

        return defaultValue;
    }


    /**
     * iterates over the blocks and loads the styles into the block when a customStyle is used, make sure this.styles is up to date before calling
     */
    private setCustomStyles(): void {
        if (!this.styles) {
            return;
        }
        this.currentAlert.blocks.forEach(block => {
            // hack @TODO NEP-3392
            if (
                block.type.key === AlertBlockTypeEnum.ALERT_MENTIONS &&
                (!block.displayElement.styles)
            ) {
                block.displayElement.styles = new MentionCustomizationModel(
                    this.getDefaultStyle(block.type.key as AlertBlockTypeEnum)
                );
            }
            // end hack
            if (block.displayElement.styles.isSavedStyle && block.displayElement.styles.isSavedStyle()) {
                const savedStyle = this.styles.find(style => style.id === block.displayElement.styles.styleState);
                if (savedStyle && block.displayElement.styles.setFromApiModel) {
                    const style: GenericStyleModel = savedStyle.clone().style;
                    if (savedStyle.template) {
                        style.template = savedStyle.template;
                    }
                    block.displayElement.styles.setFromApiModel(style);
                }
            } else if (block.displayElement.styles.styleState === BriefingStyleStateEnum.DEFAULT) {
                block.displayElement.styles.styleState = BriefingStyleStateEnum.BRIEFING_1;
                (block.displayElement.styles as any).displayStyleState = BriefingStyleStateEnum.BRIEFING_1;
            }
            if (this.getDefaultStyleSet(block.displayElement.styles.getDefaultReference())) {
                this.currentAlert.setDefaultStyles(
                    this.getDefaultStyleSet(block.displayElement.styles.getDefaultReference())[block.type.key],
                    block.displayElement.styles
                );
            }
        });

        if (this.currentAlert.generalStyles) {
            // Check to see if any of these generalStyles need populated by saved styles
            for (const genericStyleModel of this.currentAlert.generalStyles.getStyles()) {
                if (typeof genericStyleModel.styleState === 'number') {
                    const savedStyle = this.styles.find(style => style.id === genericStyleModel.styleState);
                    if (!savedStyle) {
                        // This savedStyle doesn't exist anymore?
                        continue;
                    }
                    const styleWithTemplate: GenericStyleModel = savedStyle.clone().style;
                    if (savedStyle.template) {
                        styleWithTemplate.template = savedStyle.template;
                    }
                    genericStyleModel.setFromApiModel(styleWithTemplate);
                } else if (genericStyleModel.styleState === BriefingStyleStateEnum.DEFAULT) {
                    genericStyleModel.styleState = BriefingStyleStateEnum.BRIEFING_1;
                    (genericStyleModel as any).displayStyleState = BriefingStyleStateEnum.BRIEFING_1;
                }
            }
            for (const field in this.currentAlert.generalStyles) {
                if (!this.currentAlert.generalStyles.hasOwnProperty(field)) {
                    continue;
                }
                let defaultReference: BriefingStyleStateEnum;
                if (
                    typeof this.currentAlert.generalStyles[field].styleState === 'number' ||
                    this.currentAlert.generalStyles[field].styleState === BriefingStyleStateEnum.CUSTOM
                ) {
                    defaultReference = this.currentAlert.generalStyles[field].defaultReference || BriefingStyleStateEnum.BRIEFING_1;
                } else {
                    defaultReference = this.currentAlert.generalStyles[field].styleState || BriefingStyleStateEnum.BRIEFING_1;
                }

                // if contains bad data, set it to default
                if (!this.getDefaultStyleSet(defaultReference)) {
                    defaultReference = BriefingStyleStateEnum.BRIEFING_1;
                }

                this.currentAlert.setDefaultStyles(
                    this.getDefaultStyleSet(defaultReference)[field],
                    this.currentAlert.generalStyles[field]
                );
            }
        }
    }

    /**
     * Handles changes made by the block form and updates the blocks to keep bound relationships in sync
     */
    blockFormUpdated(block: BlockModel, property: string, value: any): void {
        const isDefault: boolean = value &&
            (   value.styleState === BriefingStyleStateEnum.DEFAULT ||
                value.styleState === BriefingStyleStateEnum.BRIEFING_1 ||
                value.styleState === BriefingStyleStateEnum.BRIEFING_2 ||
                value.styleState === BriefingStyleStateEnum.BRIEFING_3
            ) && value.displayStyleState !== BriefingStyleStateEnum.CUSTOM;

        if (value && value.hasOwnProperty('styleState') && isDefault) {
            this.updateContentBlockStyle(block, 'styles.styleState', value.styleState);

            return;
        }

        if (property === 'isBoundToGlobalStyles' && value) {
            this.isBoundToGlobalStylesChange(block, property, value);

            return;
        }

        if (property !== 'styles') {
            if (property === 'automaticallyGenerated' && value === true
                && block.type.key === AlertBlockTypeEnum.ANALYSIS) {
                (block.displayElement as AnalysisDisplayElementModel).generateOnSend = true;
            }

            this.formFieldUpdated();

            return;
        }

        if (!this.blockToGeneralStylePropertiesMap) {
            this.formFieldUpdated();

            return;
        }

        const generalStylesProperty = alertBlockTypeToGeneralStylesPropertyMap.get(<AlertBlockTypeEnum>block.type.key);

        if (generalStylesProperty && block.displayElement.isBoundToGlobalStyles) {
            this.setPropertyValue(this.generalStyles, generalStylesProperty, block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, property, value);
        }

        this.formFieldUpdated();
    }

    /**
     * Doesn't need to handled currently in alerts
     */
    getUpdatedAutomaticBlock(): Observable<BlockModel> {
        return of(null);
    }

    /**
     * Handles change to the selection for isBoundToGlobalStyles
     */
    isBoundToGlobalStylesChange(block: BlockModel, property: string, value: any) {
        if (!this.blockToGeneralStylePropertiesMap || !value) {
            this.formFieldUpdated();

            return;
        }

        if (block.displayElement.isBoundToGlobalStyles) {
            const generalStyleProperty = alertBlockTypeToGeneralStylesPropertyMap.get(<AlertBlockTypeEnum>block.type.key);
            this.setPropertyValue(this.generalStyles, generalStyleProperty,  block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, 'styles', block.displayElement.styles.clone());
        }
        this.formFieldUpdated();
    }

    /**
     * Setter for generalStylesToBlocks
     */
    setGeneralStylesToBlocksMap(generalStylesToBlock: Map<string, Map<string, string>>): void {
        this.generalStylesToBlocksMap = generalStylesToBlock;
    }

    /**
     * Setter for blockToGeneralStylePropertiesMap
     */
    setBlockToGeneralStylePropertiesMap(blockGeneralStyles: Map<string, Map<string, string>>): void {
        this.blockToGeneralStylePropertiesMap = blockGeneralStyles;
    }

    /**
     * Updates the cache to reflect the current alert's state
     */
    private updateCurrentAlertCache(): void {
        if (!this.currentAlert) {
            this.currentAlertCache = null;
        }
        this.currentAlertCache = cloneDeep(this.currentAlert);
    }

    /**
     * Gets the current alert based on a route change
     */
    private async updateAlertFromRoute(route: MonitoringRouteModel): Promise<void> {
        switch (route.type) {
            case RouteType.recurringAlertReport:
            case RouteType.alert:
                if (route.value) {
                    const error = await this.setCurrentAlertFromApi(+route.value, route.subId);

                    if (error?.status === 404) {
                        // failed to load alert from the api, show feedback before navigating to
                        // alerts hype
                        this.routingService.gotoAlerts({
                            queryParams: { notFound: 'true' }
                        });
                        return;
                    }
                }
                break;

            case RouteType.alertsHype:
            default:
                // Hype page
                this.setCurrentAlert(null);
                break;
        }
        this.updateCurrentAlertCache();
    }

    /**
     * Generates a new alert
    */
    public setNewAlert(alertType: AlertTypeEnum, enableSurgeInsights: boolean = false): void {
        this.routingService.gotoAlert(null);

        let newAlert: AlertModel;

        if (alertType === AlertTypeEnum.SURGE) {
            newAlert = this.getDefaultAlert(alertType, enableSurgeInsights);
        } else {
            newAlert  = this.getDefaultAlert(alertType);
        }

        this.setBlockTypesForAlertType(alertType);
        this.setCurrentAlert(newAlert);
        this.setDefaultLanguage();
        this.setDefaultReplyToAndFromName();

        // you should be able to leave a new alert if you haven't made changes, because you aren't losing anything
        this.updateCurrentAlertCache();
    }

    /**
     * Sets the current alert to an anonymized copy of an alert
     */
    public cloneFromExistingAlert(id: number): void {
        this.briefingApi.getById(id).pipe(
            mergeMap(briefingApiModel => {
                this.currentAlert = new AlertModel(briefingApiModel).clone();

                this.setDefaultLanguage();

                // Set cache to null so it always appears dirty until saved, because if you leave a cloned alert, then you are losing work
                this.currentAlertCache = null;
                this.setCustomStyles();

                return from(this.loadFilters());

            }),
            tap(x => {
                this.currentAlert.filterSearch.id = null;
                this.currentAlert.filterSearch.name = null;
                this.setCurrentAlert(this.currentAlert);
            }),
            take(1)
        ).subscribe();
    }


    getCurrentAlert(): Observable<AlertModel> {
        return this.currentAlertProvider;
    }

    getFocusedResource(): Observable<AlertModel> {
        return this.currentAlertProvider.pipe(filter(x => !!x));
    }

    /**
     * Sets the current alert
     */
    setCurrentAlert(alert: AlertModel): void {
        this.currentAlert = alert;
        this.filterErrorSubject.next('');
        if (this.currentAlert?.blocks) {
            this.setTemplatesOnBlocks(this.currentAlert.blocks);
        }
        this.currentAlertProvider.next(alert);

        this.updateBlocks();
    }

    /**
     * Loads the mention filters
     */
    private loadFilters(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!this.currentAlert.filterSearch.id) {
                this.setCurrentAlert(this.currentAlert);
                resolve();
                return;
            }

            const blockDisplayElements = this.getAlertBlocksWithSearchCriteria().map(
                block => block.displayElement as unknown as DisplayElementWithSearchCriteriaModel
            );
            const blockSearchIds: number[] = [];
            blockDisplayElements.forEach(el => {
                if (el.searchCriteriaId && !blockSearchIds.includes(el.searchCriteriaId)) {
                    blockSearchIds.push(el.searchCriteriaId);
                }
            });

            const mainFilter$ = this.savedSearchesApi.getById(this.currentAlert.filterSearch.id);
            const blockFilters$ = blockSearchIds.length > 0
                ? this.savedSearchesApi.getSavedSearchBulk(blockSearchIds)
                : of(null);

            forkJoin([mainFilter$, blockFilters$]).subscribe({
                next: ([savedMentionSearch, savedBlockSearches]) => {
                    this.currentAlert.filterSearch.searchCriteria = new MentionSearchCriteriaModel(savedMentionSearch);
                    this.currentAlert.filterSearch.name = savedMentionSearch.name;
                    if (this.currentAlertCache) {
                        this.currentAlertCache.filterSearch = this.currentAlert.filterSearch.clone(false);
                    }

                    if (savedBlockSearches) {
                        blockDisplayElements.forEach(el => {
                            if (el.searchCriteriaId && savedBlockSearches[el.searchCriteriaId]) {
                                el.setSearchCriteria(new MentionSearchModel(savedBlockSearches[el.searchCriteriaId]));
                            }
                        });
                    }

                    this.setCurrentAlert(this.currentAlert);
                    resolve();
                },
                error: () => reject()
            });
        });
    }

    private getAlertBlocksWithSearchCriteria(): BlockModel[] {
        const result: BlockModel[] = [];
        this.currentAlert.blocks?.forEach(block => {
            if (block.displayElement.automaticallyGenerated) {
                return;
            }
            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                result.push(block);
                const curatorBlock = block as CuratorBlock;
                [...(curatorBlock.blocks ?? []), ...(curatorBlock.originalBlocks ?? [])].forEach(innerBlock => {
                    if ((isMentionBlockTypes(innerBlock.type) || innerBlock.type.key === BriefingBlockTypeEnum.ANALYSIS)
                        && !block.displayElement.automaticallyGenerated) {
                        result.push(innerBlock);
                    }
                });
            } else if (isMentionBlockTypes(block.type) || block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                result.push(block);
            }
        });
        return result;
    }

    /**
     * Fetches an alert from the backend and sets it as the current alert
     */
    private async setCurrentAlertFromApi(id: number, subId?: string, briefingApiModel?: BriefingApiModel): Promise<any> {
        try {
            const briefing$ = briefingApiModel
                ? of(briefingApiModel)
                : this.briefingApi.getById(id);
            const result = await lastValueFrom(
                combineLatest([
                    briefing$,
                    this.hasDefaultTemplates(),
                    this.stylesService.all$.pipe(filter(x => x !== null))
                ]).pipe(take(1))
            );

            const newBriefingApiModel = result[0];
            this.currentAlert = new AlertModel(newBriefingApiModel);

            if (subId) {
                this.getSingleHistory(subId);
            }

            this.setDefaultLanguage();

            this.setTemplatesOnBlocks(this.currentAlert.blocks);

            this.styles = result[2];
            this.setCustomStyles();

            await this.loadFilters();
            this.lockCuratorChildSource();
            this.setBlockTypesForAlertType(this.currentAlert.alertType);
            this.currentAlertCache = cloneDeep(this.currentAlert);
            this.setCurrentAlert(this.currentAlert);
            this.selectBlock(null);
        } catch (error) {
            return error;
        }
    }

    private setBlockTypesForAlertType(alertType: AlertTypeEnum): void {
        if (alertType === AlertTypeEnum.SURGE) {
            this.setBlockTypes(alertsBlockTypes);
            this.checkAnalysisBlockBeforeSettingBlockTypeGroups(alertType);
        } else {
            this.setBlockTypes(recentAlertsBlockTypes);
            this.setBlockTypeGroups(recentAlertsBlockTypeGroups);
        }
    }

    private checkAnalysisBlockBeforeSettingBlockTypeGroups(alertType?: AlertTypeEnum): void {
        if (alertType !== AlertTypeEnum.SURGE) {
            return;
        }
        if (!this.hasCoverageInsightsEnabled) {
            const blockTypeGroups = cloneDeep(alertsBlockTypeGroups);
            blockTypeGroups[0].blockTypes.map(
                type => type.isLocked = type.key === BriefingBlockTypeEnum.ANALYSIS || type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR
            );
            this.setBlockTypeGroups(blockTypeGroups);
        } else {
            this.setBlockTypeGroups(alertsBlockTypeGroups);
        }
    }

    lockCuratorChildSource(): void {
        this.currentAlert.blocks.forEach(block => {
            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                const curatorBlock = block as CuratorBlock;
                curatorBlock.blocks.forEach(child => {
                    if ((child.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || child.type.key === BriefingBlockTypeEnum.ANALYSIS)
                        && child.displayElement.automaticallyGenerated
                    ) {
                        const subBlockDisplayElement = child.displayElement as DynamicMentionListDisplayElementModel | AnalysisDisplayElementModel;
                        subBlockDisplayElement.backendDataSource = curatorBlock.displayElement.backendDataSource;
                        subBlockDisplayElement.searchCriteriaId = curatorBlock.displayElement.searchCriteriaId;
                        subBlockDisplayElement.sourceType = curatorBlock.displayElement.sourceType;
                        subBlockDisplayElement.filterMentionSearchModel = curatorBlock.displayElement.filterMentionSearchModel;
                    }
                });
            }
        });
    }

    getSingleHistory(subId: string) {
        let singleHistReq = this.briefingApi.getAlertSendHistory(subId).subscribe({
            next: (alertHistory) => {
                this.alertHistory = new AlertHistoryModel(alertHistory);
                this.alertHistory$.next(this.alertHistory);
                singleHistReq.unsubscribe();
                singleHistReq = null;
            },
            error: (error) => {
                this.alertHistory$.next(new AlertHistoryModel());
            }
        });
    }

    getAlertHistory() {
        return this.alertHistory$;
    }

    clearAlertHistory() {
        this.alertHistory = null;
        this.alertHistory$.next(this.alertHistory);
    }

    getAlertSourceChanged() {
        return this.alertSourceChanged$;
    }

    setAlertSourceChanged(sourceChanged: boolean) {
        this.alertSourceChanged = sourceChanged;
        this.alertSourceChanged$.next(this.alertSourceChanged);
    }
    /**
     * Gets the list of alerts
     */
    public getAlertsList(): Observable<BriefingListItemModel[]> {
        return this.alertsListProvider;
    }

    public getFilterListItems(): Observable<AuditableModel[]> {
        return this.getAlertsList();
    }

    /**
     * Updates the alerts list
     */
    private setAlertsList(alerts: BriefingListItemModel[]): void {
        this.alertsList = alerts;
        this.alertsListProvider.next(this.alertsList);
    }

    /**
     * Refreshes the alerts list when it becomes stale
     */
    refreshAlertsList(visibilityButton?: LeftSidebarListItemButton): void {
        this.briefingApi.getList(BriefingTypeEnum.ALERT).subscribe((alerts) => {
            const alertsList = alerts.map(alert => new BriefingListItemModel(alert));
            this.setAlertsList(alertsList);
            if (visibilityButton) {
                visibilityButton.loading = false;
            }
        });
    }

    /**
     * Gets an alert with the default settings
     */
    private getDefaultAlert(alertType?: AlertTypeEnum, enableSurgeInsights: boolean = false): AlertModel {
        const newAlert: AlertModel = new AlertModel();

        newAlert.setGeneralStyles(this.getDefaultStyleSet(BriefingStyleStateEnum.BRIEFING_1));

        const defaultGeneralStyles: any = this.getDefaultStyleSet(BriefingStyleStateEnum.BRIEFING_1);
        this.setDefaultGeneralStylesWithUserPreferences(defaultGeneralStyles);
        newAlert.setGeneralStyles(defaultGeneralStyles);

        newAlert.alertType = alertType;

        // Add default blocks.
        let blockType: BlockTypeModel;
        let block: BlockModel;

        // Image
        let url = '';
        if (alertType === AlertTypeEnum.SURGE) {
            if (this.languageTag === LanguageTagEnum.fr_CA || this.languageTag === LanguageTagEnum.fr_FR) {
                url = 'https://public.agilitypr.media/adccd165-bfd9-4c24-a25b-e2bc7397b48f';
            } else {
                url = 'https://public.agilitypr.media/be420990-10e3-492f-92ce-288b68875990';
            }
        } else {
            if (this.languageTag === LanguageTagEnum.fr_CA || this.languageTag === LanguageTagEnum.fr_FR) {
                url = 'https://public.agilitypr.media/2cc3734d-f48c-49f6-ac3e-a910e0d64abd';
            } else {
                url = 'https://public.agilitypr.media/28998816-4cac-4f9f-b784-a11aa2b62711';
            }
        }

        blockType = imageBlockType;
        block = new BlockModel(
            null,
            blockType,
            new BriefingImageDisplayElementModel(
                <BriefingImageDisplayElementApiModel>{
                    styles: this.getDefaultStyle(AlertBlockTypeEnum.IMAGE, BriefingStyleStateEnum.BRIEFING_1),
                    images: [{
                        url
                    }]
                }
            ),
            'coverageAlert.image'
        );
        (block.displayElement as BriefingImageDisplayElementModel).onUpdate();
        newAlert.blocks.push(block);

        // Date
        blockType = alertDateBlockType;
        block = new BlockModel(
            null,
            blockType,
            new DateDisplayElementModel(
                undefined,
                undefined,
                this.getDefaultStyle(AlertBlockTypeEnum.DATE, BriefingStyleStateEnum.BRIEFING_1)
            ),
            'coverageAlert.date'
        );
        this.localizeDateBlock(block);
        newAlert.blocks.push(block);

        // Heading
        blockType = titleBlockType;
        block = new BlockModel(
            null,
            blockType,
            new BriefingTitleDisplayElementModel(
                this.localizationService.transform('coverageAlert.heading'),
                this.getDefaultStyle(AlertBlockTypeEnum.TITLE, BriefingStyleStateEnum.BRIEFING_1)
            ),
            'coverageAlert.heading'
        );
        if (alertType === AlertTypeEnum.SURGE) {
            (block.displayElement as TextDisplayElementModel).text = this.localizationService.transform('Alerts.defaultTitle.surge');
        } else {
            (block.displayElement as TextDisplayElementModel).text = this.localizationService.transform('Alerts.defaultTitle');
        }
        newAlert.blocks.push(block);

        // Mentions
        if (alertType === AlertTypeEnum.SURGE) {
            blockType = alertSurgeBlockType;
        } else {
            blockType = alertMentionListBlockType;
        }

        block = new BlockModel(
            null,
            blockType,
            new (blockType.displayElementType)(),
            'coverageAlert.mentionList'
        );
        block.canAddOrRemove = false;
        block.canDuplicate = false;
        block.canEdit = false;

        if (this.preferences) {
            if (newAlert.generalStyles?.mention) {
                newAlert.generalStyles.mention.timezone = this.preferences.timeZone;
            }
            block.displayElement.styles.timezone = this.preferences.timeZone;
            newAlert.sendAt.timeZone = this.preferences.timeZone;
        }

        block.displayElement.styles.locale = this.languageTag;
        newAlert.blocks.push(block);

        // Add curator block if surge insights are enabled
        if (alertType === AlertTypeEnum.SURGE && enableSurgeInsights) {
            const curatorBlock = this.createCuratorBlock(surgeAlertAiCuratorBlockType, {});
            newAlert.blocks.push(curatorBlock);
        }

        return newAlert;
    }


    /**
     * Shortcut to just update the blocks of the current alert
     */
    public setCurrentAlertBlocks(blocks: BlockModel[]): void {
        this.currentAlert.blocks = blocks;
        this.setCurrentAlert(this.currentAlert);
    }

    /**
     * Makes the current alert "clean" by making it's cache value match it
     * @note primary use of this method is to allow navigating away from the current alert
     */
    makeCurrentAlertClean(): void {
        this.updateCurrentAlertCache();
    }

    /**
     * Checks if the current alert has changed since it was fetched
     */
    public currentAlertIsUnchanged(): boolean {
        if (!this.currentAlert) {
            return true;
        }

        return this.currentAlert.equals(this.currentAlertCache);
    }

    /**
     * Verifies that the current editing alert is in a state where the user can leave without losing data
     */
    canLeaveCurrentAlert(): boolean {
        return this.currentAlertIsUnchanged();
    }

    openNewAlertDialog() {
        this.coreDialogService.open(AlertCreateDialogComponent, {
            inputs: {
                id: 'alert.createAlert',
                heading: 'Alerts.amplify.createBriefing',
            },
            outputs: {
                dialogClose: (result: AlertTypeEnum | { alertType: AlertTypeEnum; enableSurgeInsights: boolean }) => {
                    let alertType: AlertTypeEnum;
                    let enableSurgeInsights = false;

                    // Handle both old format (AlertTypeEnum) and new format (object)
                    if (typeof result === 'object' && result !== null && 'alertType' in result) {
                        alertType = result.alertType;
                        enableSurgeInsights = result.enableSurgeInsights || false;
                    } else {
                        alertType = result as AlertTypeEnum;
                    }

                    switch (alertType) {
                        case AlertTypeEnum.RECENT:
                            this.setNewAlert(alertType);
                            break;
                        case AlertTypeEnum.SURGE:
                            this.setNewAlert(alertType, enableSurgeInsights);
                            break;
                        default:
                            break;
                    }
                }
            }
        });
    }

    /**
     * Shows a dialog to verify that the user is aware their changes will be lost
     */
    showCanLeaveDialog(): Observable<boolean> {
        if (this.canLeaveCurrentAlert()) {
            return of(true);
        }

        return new Observable(
            (observer: Observer<boolean>) => {
                const io: ConfirmDialogModel = {
                    inputs: {
                        heading: 'DeactivateGuard.heading',
                        messageHtml: 'DeactivateGuard.message',
                        okLabel: 'continue',
                        id: 'AlertsContinue'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            observer.next(confirmed);
                            observer.complete();
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);
            }
        );
    }

    saveAlertSavedSearches(): Observable<any> {
        if (this.currentAlert.filterSearch.searchCriteria.query.dateQuery && !this.currentAlert.filterSearch.searchCriteria.query.dateQuery.timezone) {
            this.currentAlert.filterSearch.searchCriteria.query.dateQuery.timezone = this.userTimezonePreference;
        }

        const requests: Observable<any>[] = [];

        if (this.currentAlert.filterSearch.id) {
            requests.push(this.savedSearchesApi.update(this.currentAlert.filterSearch.toApiModel()));
        } else {
            if (!this.currentAlert.filterSearch.name) {
                // Name can't be blank, must be unique
                this.currentAlert.filterSearch.name = `${+new Date()}${Math.random()}`;
            }
            this.currentAlert.type = BriefingTypeEnum.ALERT;
            requests.push(
                this.savedSearchesApi.create(this.currentAlert.filterSearch.toApiModel()).pipe(
                    tap((id) => this.currentAlert.filterSearch.id = id)
                )
            );
        }

        this.getAlertBlocksWithSearchCriteria().forEach(block => {
            const el = block.displayElement as unknown as DisplayElementWithSearchCriteriaModel;
            if ((block.displayElement as any).useSearchDateFilter !== true || typeof el.getSearchCriteria !== 'function') {
                return;
            }
            const searchCriteria = el.getSearchCriteria();
            if (!searchCriteria) {
                return;
            }
            if (!el.searchCriteriaId) {
                if (!searchCriteria.name) {
                    // Name can't be blank, must be unique
                    searchCriteria.name = `${+new Date()}${Math.random()}`;
                }
                searchCriteria.type = SavedSearchType.ALERT;
                requests.push(
                    this.savedSearchesApi.create(searchCriteria.toApiModel()).pipe(
                        tap(id => {
                            el.searchCriteriaId = id;
                            searchCriteria.id = id;
                        })
                    )
                );
            } else {
                searchCriteria.id = el.searchCriteriaId;
                requests.push(this.savedSearchesApi.update(searchCriteria.toApiModel()));
            }
        });

        return forkJoin(requests);
    }

    /**
     * Saves the changes of current alert to the backend
     */
    saveCurrentAlert(hideMessage: boolean = false): Observable<boolean> {
        return this.validateFilters().pipe(
            mergeMap(validate => {
                if (!validate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Alerts.saveAlertErrorHeading',
                        detail: this.localizationService.transform('Alerts.saveAlertErrorMessage.invalidFilter')
                    });

                    return of(false);
                }

                return this.saveAlertSavedSearches().pipe(
                    mergeMap(() => {
                        return this.saveToMediaService();
                    }),
                    mergeMap(() => {
                        return this.briefingApi.save(this.currentAlert.toApiModel());
                    }),
                    mergeMap((alert) => {
                        // Update the list in case save added a new one or changed the name of an existing one
                        this.refreshAlertsList();

                        if (this.currentAlert.id) {
                            // Update case
                            this.setCurrentAlertFromApi(this.currentAlert.id, null, alert);
                        } else {
                            // Create case
                            this.currentAlert.id = alert.briefingId;

                            // Copy the current briefing to the cached briefing because they should now be in sync
                            this.currentAlertCache = cloneDeep(this.currentAlert);
                            this.routingService.gotoAlert(alert.briefingId);
                        }

                        if (!hideMessage) {
                            this.messageService.add({
                                severity: messageSeverityEnum.success,
                                summary: 'Alerts.saveAlertSuccessHeading',
                                detail: this.localizationService.transform('Alerts.saveAlertSuccessMessage', {
                                    name: this.currentAlert.name
                                })
                            });
                        }

                        return of(true);
                    })
                );
            }),
            catchError((error) => {
                this.refreshAlertsList();

                if (
                    error
                    && error.error
                    && error.error.details?.[0].code === BriefingsApiErrorEnum.nameDuplicate
                ) {
                    // Duplicate name error case
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Alerts.saveAlertErrorHeading',
                        detail: this.localizationService.transform('Alerts.saveAlertDuplicateNameErrorMessage', {
                            name: this.currentAlert.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Alerts.saveAlertErrorHeading',
                        detail: this.localizationService.transform('Alerts.saveAlertErrorMessage', {
                            name: this.currentAlert.name
                        })
                    });
                }

                throw error;
            })
        );
    }



    private saveToMediaService(): Observable<boolean> {
        const requests: Observable<boolean>[] = [];
        this.currentAlert?.blocks.forEach(block => {
            this.checkAndCreateImage(block, requests);
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.checkAndCreateImage(x, requests);
                    });
                });
            }
        });

        if (requests?.length) {
            return combineLatest(requests).pipe(map(validateMedia => {
                return !validateMedia.some(x => x === false);
            }));
        } else {
            return of(true);
        }
    }

    private checkAndCreateImage(block: BlockModel, requests: Observable<boolean>[]): void {
        const displayElement = block.displayElement as BriefingChartDisplayElementModel;

        if (block.type.key === BriefingBlockTypeEnum.CHART) {
            if (displayElement.chart.generateOnSend) {
                if (displayElement?.chart?.chartUrl && !displayElement?.chart?.chartUrl?.includes('agilitypr.media')) {
                    delete displayElement.chart.chartUrl;
                }
                return;
            }
            if (displayElement?.chart?.chartUrl && !displayElement?.chart?.chartUrl?.includes('agilitypr.media')) {
                const file = base64ToFile(displayElement.chart.chartUrl, `briefing_chart_${Date.now()}`);

                delete displayElement.chart.chartUrl;

                requests.push(
                    this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC, 'false').pipe(
                        map((result) => {
                            const imageMediaResult: MediaResultModel = result?.mediaResults?.find(mediaResult => {
                                return (mediaResult?.media?.name === file.name) || (mediaResult?.metadata?.submissionName === file.name);
                            });

                            if (imageMediaResult) {
                                const url = imageMediaResult?.media?.url;
                                const errorCode = imageMediaResult?.codedError?.code;

                                if (errorCode) {
                                    this.handleMediaErrorCode(errorCode);
                                    return (false);
                                }
                                if (url) {
                                    displayElement.chart.chartUrl = url;
                                } else {
                                    return (false);
                                }
                            } else {
                                const error = result.mediaResults?.find(mediaResult => !!mediaResult.codedError);
                                this.handleMediaErrorCode(error?.codedError?.code);
                                return false;
                            }
                            return true;
                        }),
                        catchError(error => {
                            if (error.status === 429) {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Common.mediaService.fileUpload.notEnoughSpace.heading',
                                    detail: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.message', { name: file.name })
                                });
                                return of(false);
                            }
                            return throwError(() => error);
                        })
                    )
                );
            }
        }
    }

    handleMediaErrorCode(errorCode: MediaServiceErrorCodesEnum) {
        if (errorCode === MediaServiceErrorCodesEnum.insufficientSpace) {
            const confirmDialogModel: ConfirmDialogModel = {
                inputs: {
                    heading: 'PageBuilder.error.imageLimitHeader',
                    messageHtml: 'PageBuilder.error.imageLimitMessageHtml',
                    alertType: messageSeverityEnum.error,
                    okLabel: 'PageBuilder.error.imageLimitOkLabel',
                    showCancel: false
                },
                outputs: {
                    dialogClose: () => {}
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);

            return;
        }

        if (errorCode === MediaServiceErrorCodesEnum.fileMaxSize) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'PageBuilder.error.fileSizeHeader',
                detail: 'PageBuilder.error.fileSizeMessage'
            });
            return;
        }

        if (errorCode !== null) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'PageBuilder.error.imageUploadFailed',
                detail: 'PageBuilder.error.imageUploadFailedMessage'
            });
        }
    }

    private validateFilters(): Observable<boolean> {
        let request: Observable<boolean>;

        if (
            !this.currentAlert.alertIsSubscriptionSearch
            && !this.currentAlert.alertIsAgentSearch
            && !this.currentAlert.alertIsAiAgentSearch
            && !this.currentAlert.alertIsThemeSearch
            && this.currentAlert.searchCriteriaId
            && this.topics.find(x => x.id === this.currentAlert.searchCriteriaId)
        ) {
            request = this.savedSearchesApi.getById(this.currentAlert.searchCriteriaId)
                .pipe(
                    mergeMap(savedSearchApiModel => {
                        const mentionSearchModel = new MentionSearchModel(savedSearchApiModel);
                        mentionSearchModel.searchCriteria.filterQuery = this.currentAlert.filterSearch.searchCriteria.query;
                        if (mentionSearchModel.searchCriteria.filterQuery.dateQuery) {
                            mentionSearchModel.searchCriteria.filterQuery.dateQuery.timezone = this.userTimezonePreference;
                        }
                        return this.mentionsApi.validateFilters(mentionSearchModel.searchCriteria.getApiEntity());
                    }),
                    map(() => true),
                    catchError(error => {
                        if (error.status === 400) {
                            let message = 'Alerts.error.invalidFilter';

                            if (error.error.details?.length) {
                                message = error.error.details[0].message;
                            }

                            this.filterErrorSubject.next(message);

                            return of(false);
                        }

                        throw error;
                    })
                );
        }

        if (request) {
            return request;
        } else {
            return of(true);
        }
    }

    /**
     * Tells the backend to activate the current alert
     */
    activateCurrentAlert(): Observable<null> {
        const request = this.briefingApi.send(this.currentAlert.id);

        request.subscribe({
            next: () => {
                let successSummary = 'Alerts.activateAlertSuccessHeading';
                let successMsg = 'Alerts.activateAlertSuccessMessage';
                if (this.currentAlert.sendAt.range) {
                    successSummary = 'Alerts.activateAlertRangeSuccessHeading';
                    successMsg = 'Alerts.activateAlertRangeSuccessMessage';
                }
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform(successSummary),
                    detail: this.localizationService.transform(successMsg, {name: this.currentAlert.name })
                });

                // Update the list to show the new status of the briefing
                this.refreshAlertsList();
                this.setCurrentAlertFromApi(this.currentAlert.id);
            },
            error: (error) => {
                let errorFound = false;
                if (error && error.error && error.error.details) {
                    for (const errorDetail of error.error.details) {
                        if (errorDetail.code === CommonApiErrorEnum.badReference) {
                            errorFound = true;
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Alerts.activateAlertErrorHeading',
                                detail: this.localizationService.transform('Alerts.activateAlertErrorMessage.topicGone', { name: this.currentAlert.name })
                            });
                            // If the cache has the bad searchCriteriaId, clear it too, so it's not an unsaved change
                            if (this.currentAlertCache.searchCriteriaId === this.currentAlert.searchCriteriaId) {
                                this.currentAlertCache.searchCriteriaId = null;
                            }
                            this.currentAlert.searchCriteriaId = null;
                            break;
                        }
                    }
                }
                if (!errorFound) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Alerts.activateAlertErrorHeading',
                        detail: this.localizationService.transform('Alerts.activateAlertErrorMessage.generic', { name: this.currentAlert.name })
                    });
                }
                this.refreshAlertsList();
            }
        });

        return request;
    }

    /**
     * Handles changes to briefing block content style change
     */
    updateContentBlockStyle(block: BlockModel, property: string, value: any) {
        switch (value) {
            case BriefingStyleStateEnum.DEFAULT:
            case BriefingStyleStateEnum.BRIEFING_1:
            case BriefingStyleStateEnum.BRIEFING_2:
            case BriefingStyleStateEnum.BRIEFING_3:
                const defaultStyle = this.getDefaultStyle(<AlertBlockTypeEnum>block.type.key, value);
                this.setPropertyValue(block.displayElement, 'styles', defaultStyle.clone(block.displayElement.styles));
                break;
            case BriefingStyleStateEnum.CUSTOM:
                break;
            default:
                break;
        }

        if (!this.blockToGeneralStylePropertiesMap) {
            this.formFieldUpdated();

            return;
        }

        if (block.displayElement.isBoundToGlobalStyles) {
            const generalStyleProperty = alertBlockTypeToGeneralStylesPropertyMap.get(<AlertBlockTypeEnum>block.type.key);
            this.setPropertyValue(this.generalStyles, generalStyleProperty,  block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, 'styles', block.displayElement.styles.clone());
        }

        block.displayElement.styles = block.displayElement.styles.clone();
        this.formFieldUpdated();
        this.selectBlock(block);
    }

    /**
     * Tells the backend to pause (cancel) the current alert
     */
    pauseCurrentAlert(): Observable<null> {
        const request = this.briefingApi.cancel(this.currentAlert.id);

        request.subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Alerts.pauseSuccessHeading',
                    detail: this.localizationService.transform('Alerts.pauseSuccessMessage', { name: this.currentAlert.name })
                });

                this.refreshAlertsList();
                this.setCurrentAlertFromApi(this.currentAlert.id);
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Alerts.pauseErrorHeading',
                    detail: this.localizationService.transform('Alerts.pauseErrorMessage', { name: this.currentAlert.name })
                });

                this.refreshAlertsList();
            }
        });

        return request;
    }

    /**
     * Renews (re-activates) the current alert
     * @TODO this may need to change as it's only available on expired alerts,
     *       so the time range would already be past, making it immediately expire again
     */
    renewCurrentAlert(): Observable<null> {
        return this.activateCurrentAlert();
    }

    /**
     * Deletes the specified alert
     */
    deleteAlert(alert): Observable<number> {
        const request = this.briefingApi.deleteById(alert.id);
        request.subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Alerts.deleteAlertSuccessHeading',
                    detail: this.localizationService.transform('Alerts.deleteAlertSuccessMessage', {
                        name: alert.name
                    })
                });

                if (this.currentAlert && alert.id === this.currentAlert.id) {
                    this.routingService.gotoAlerts();
                }

                // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                this.refreshAlertsList();
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Alerts.deleteAlertErrorHeading',
                    detail: this.localizationService.transform('Alerts.deleteAlertErrorMessage', {
                        name: alert.name
                    })
                });
            }
        });
        return request;
    }

    /**
     * Prompts the user to confirm before deleting the specified alert
     */
    confirmDeleteAlert(alert: BriefingListItemModel): Observable<boolean> {
        return new Observable((subscriber) => {
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Alerts.deleteCoverageAlert',
                    message: this.localizationService.transform('Alerts.confirmDeleteAlertMessage', { name: alert.name }),
                    alertMessage: this.localizationService.transform('Alerts.confirmDeleteAlert'),
                    alertType: messageSeverityEnum.warning,
                    okLabel: 'Core.button.delete',
                    id: 'AlertsDelete'
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.deleteAlert(alert).subscribe({
                                next: () => {
                                    subscriber.next(true);
                                    subscriber.complete();
                                },
                                error: (error) => {
                                    subscriber.error(error);
                                    subscriber.complete();
                                }
                            });
                        } else {
                            this.refreshAlertsList();
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        });
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    public confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const io: ConfirmDialogModel = {
                inputs: {
                    id: 'AlertsBulkDelete',
                    heading: 'Alerts.bulkDeleteCoverageAlert',
                    alertType: messageSeverityEnum.warning,
                    alertMessage: 'Alerts.bulkConfirmDeleteAlert.alertMessage',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Alerts.bulkConfirmDeleteAlert.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDeleteAlert(ids);
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        });

    }

    private bulkDeleteAlert(ids: number[]) {
        const observables = ids.map(id => {
            return this.briefingApi.deleteById(id);
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Alerts.bulkDeleteAlertSuccessHeading',
                    detail: this.localizationService.transform('Alerts.bulkDeleteAlertSuccessMessage', {
                        number: result.length
                    })
                });

                if (this.currentAlert && ids.includes(this.currentAlert.id)) {
                    this.routingService.gotoAlerts();
                }

                this.refreshAlertsList();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Alerts.bulkDeleteAlertErrorHeading',
                    detail: 'Alerts.bulkDeleteAlertErrorMessage'
                });
                this.refreshAlertsList();
            }
        });
    }


    confirmCopyAlert(alert: BriefingListItemModel){
        const inputConfig: NewResourceDialogModel = {
            inputs: {
                heading: 'Alerts.copyAlert',
                initialValue: alert.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: alert.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                formValidator: (name: string) => {
                    if (this.alertsList.find(a => a.name === name.trim())) {
                        return [
                            'Alerts.copyAlertDuplicateNameErrorMessage'
                        ];
                    } else {
                        return [];
                    }
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    this.briefingApi.getById(alert.id).pipe(
                        mergeMap(_briefing => {
                            const uniqueSearchIds = [_briefing.filterSearchCriteriaId];

                            _briefing.blocks.forEach(block => {
                                if (block.type === BriefingBlockTypeApiEnum.IMAGE) {
                                    // eslint-disable-next-line max-len
                                    if ((block as BriefingImageDisplayElementApiModel).imageProperties?.url === 'https://public.agilitypr.media/8727c740-447d-4b9c-9b38-388472f54687') {
                                        // eslint-disable-next-line max-len
                                        (block as BriefingImageDisplayElementApiModel).imageProperties.url = 'https://public.agilitypr.media/be420990-10e3-492f-92ce-288b68875990';
                                    }

                                    // eslint-disable-next-line max-len
                                    if ((block as BriefingImageDisplayElementApiModel).imageProperties?.url === 'https://apigateway.agilitypr.com/api/v1/images/7dbec9a0-635c-4de8-9d5c-584e9bf50745') {
                                        // eslint-disable-next-line max-len
                                        (block as BriefingImageDisplayElementApiModel).imageProperties.url = 'https://public.agilitypr.media/28998816-4cac-4f9f-b784-a11aa2b62711';
                                    }
                                }
                            });

                            // Search for all the saved searches associated with this briefing
                            return this.savedSearchesApi.getSavedSearchBulk(uniqueSearchIds).pipe(
                                map(savedSearches => {
                                    return {
                                        _briefing: _briefing,
                                        savedSearches: savedSearches
                                    };
                                })
                            );
                        }),
                        mergeMap(combinedResponse => {
                            const _briefing = combinedResponse._briefing;
                            _briefing.briefingSubject = _briefing.briefingSubject?.replace(_briefing.briefingName, name);
                            _briefing.briefingName = name;
                            _briefing.status = BriefingStatusApiEnum.DRAFT;
                            _briefing.displayGroupId = group?.id;
                            delete _briefing.briefingId;

                            const searchCriteriaToBeCloned = combinedResponse.savedSearches[_briefing.filterSearchCriteriaId];
                            searchCriteriaToBeCloned.name = `${+new Date()}${Math.random()}`;
                            delete searchCriteriaToBeCloned.id;

                            return this.savedSearchesApi.create(searchCriteriaToBeCloned).pipe(
                                tap(
                                    searchCriteriaCloneId => {
                                        _briefing.filterSearchCriteriaId = searchCriteriaCloneId;
                                    }
                                ),
                                map(clonedSavedSearches => {
                                    return {
                                        _briefing: _briefing,
                                        savedSearches: combinedResponse.savedSearches,
                                        clonedSavedSearches: clonedSavedSearches
                                    };
                                })
                            );
                        }),
                        mergeMap(combinedResponse => {
                            return this.briefingApi.createFull(combinedResponse._briefing);
                        })
                    ).subscribe({
                        next: (x) => {
                            this.messageService.add({
                                severity : messageSeverityEnum.success,
                                summary: 'Alerts.copyAlert',
                                detail: 'Discover.confirmDialog.copy.success'
                            });
                            // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                            this.refreshAlertsList();
                            this.routingService.gotoAlert(x.briefingId);
                        }, error: (err) => {
                            if (err?.error?.details[0]?.code === BriefingsApiErrorEnum.nameDuplicate) {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Alerts.renameAlertErrorHeading',
                                    detail: this.localizationService.transform('Alerts.saveAlertDuplicateNameErrorMessage', { name: name })
                                });
                            } else {
                                this.messageService.add({
                                    severity : messageSeverityEnum.error,
                                    summary: 'Alerts.copyAlert',
                                    detail: 'Discover.confirmDialog.copy.error'
                                });
                            }
                        }
                    });

                    return of(null);
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, inputConfig);
    }

    /**
     * Adds alerts to a group (or removes the group id if Uncategorized is the selected group)
     * @param alertIds
     * @param groupId ID of the group or undefined if Uncategorized is selected
     */
    public updateGroup(alertIds: number[], group: DisplayGroupModel) {
        let request;
        if (group.id) {
            request = this.briefingApi.updateBriefingDisplayGroup(alertIds, [], group.id, BriefingTypeEnum.ALERT);
        } else {
            request = this.briefingApi.removeBriefingsFromDisplayGroup(alertIds, BriefingTypeEnum.ALERT);
        }
        request.subscribe({
            next: () => {
                this.refreshAlertsList();
                if (alertIds.length === 1) {
                    const itemName = this.alertsList.find(alert => alert.id === alertIds[0]).name;
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.single',
                            {itemName: itemName, groupName: group.name}
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.multiple',
                            {number: alertIds.length, name: group.name}
                        )
                    });
                }
            },
            error: (err) => {
                this.refreshAlertsList();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    /**
     * Removes the group ID from all provided alerts
     * @param ids alert IDs
     */
    public removeAllAlertsFromGroup(ids: number[]) {
        this.briefingApi.removeBriefingsFromDisplayGroup(ids, BriefingTypeEnum.ALERT).subscribe({
            next: () => {
                this.refreshAlertsList();
            }
        });
    }

    public updateFavorites(addIds: number[], removeIds: number[]) {
        this.briefingApi.updateBriefingFavorites(addIds, removeIds, BriefingTypeEnum.ALERT).subscribe({
            next: () => {
                this.refreshAlertsList();
            },
            error: (err) => {
                this.refreshAlertsList();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });
            }
        });
    }

    /**
     * Sets up the default 'reply to' email address and 'from name' value based on the logged in user.
     */
    private setDefaultReplyToAndFromName(): void {
        if (!this.user || !this.currentAlert) {
            return;
        }

        // Set the default 'reply to' if the alert doesn't already have a value for it.
        if (this.user.email && !this.currentAlert.replyTo) {
            this.currentAlert.replyTo = this.user.email;

            // Update the cached alert.
            if (this.currentAlertCache) {
                this.currentAlertCache.replyTo = this.user.email;
            }
        }

        // Set the default 'from name' if the alert doesn't already have a value for it.
        const userDisplayName: string = this.user.getDisplayName();
        if (userDisplayName && !this.currentAlert.fromName) {
            this.currentAlert.fromName = userDisplayName;

            // Update the cached alert.
            if (this.currentAlertCache) {
                this.currentAlertCache.fromName = userDisplayName;
            }
        }
    }

    /**
     * Checks if the provided string matches the name of a alert
     *
     * @param name - name to check
     *
     * @returns boolean
     */
    public isAlertNameDuplicate(name: string, id?: number) {
        if (!this.alertsList) {
            return false;
        }
        const duplicateAlert = this.alertsList.find(listItem => {
            if (id) {
                return listItem.name.toLocaleLowerCase() === name.toLocaleLowerCase() && listItem.id !== id;
            }
            return listItem.name.toLocaleLowerCase() === name.toLocaleLowerCase();
        });
        if (duplicateAlert) {
            return true;
        }
        return false;
    }

    public updateAlertVisibility(id: number, action: ActionsEnum, button: LeftSidebarListItemButton): void {
        const hidden = action === ActionsEnum.hide;
        button.loading = true;
        this.briefingApi.getById(id).subscribe(
            briefing => {
                briefing.hidden = hidden;
                this.briefingApi.updateFull(briefing).subscribe({
                    next: () => this.refreshAlertsList(button),
                    error: () => button.loading = false
                });
            }
        );
    }

    public bulkUpdateVisibility(ids: number[], action: ActionsEnum) {
        const hidden = action === ActionsEnum.hide;
        const observables = ids.map(id => {
            return this.briefingApi.getById(id).pipe(
                switchMap(alert => {
                    alert.hidden = hidden;
                    return this.briefingApi.updateFull(alert);
                })
            );
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Alerts.bulkUpdateAlertSuccessHeading',
                    detail: this.localizationService.transform('Alerts.bulkUpdateAlertSuccessMessage', {
                        number: result.length
                    })
                });
                this.refreshAlertsList();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Alerts.bulkUpdateAlertErrorHeading',
                    detail: 'Alerts.bulkUpdateAlertErrorMessage'
                });
                this.refreshAlertsList();
            }
        });
    }

    public openRenameAlertDialog(alert: BriefingListItemModel) {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'renameAlert',
                heading: 'Alerts.renameAlertDialog.heading',
                description: 'Alerts.renameAlertDialog.description',
                placeholder: 'Alerts.renameAlertDialog.placeholder',
                initialValue: alert.name,
                okLabel: 'Lhp.actions.rename',
                formValidator: (name: string) => {
                    if (this.isAlertNameDuplicate(name, alert.id)) {
                        return [
                            'Alerts.copyAlertDuplicateNameErrorMessage'
                        ];
                    } else {
                        return [];
                    }
                },
                submit: (name: string) => {
                    this.updateAlertName(alert.id, name);
                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }


    public renameAlert(alert: BriefingListItemModel): void {
        const io: TextInputDialogModel = {
            inputs: {
                heading: 'Alerts.renameAlertSuccessHeading',
                okLabel: 'Lhp.actions.rename',
                placeholder: 'Alerts.input.alert.rename.placeholder',
                description: 'Alerts.input.alert.rename.name',
                initialValue: alert.name,
                id: 'renameAlert',
                submit: (name: string) => {
                    this.updateAlertName(alert.id, name);
                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    public updateAlertName(id: number, name: string) {
        let oldName;
        this.briefingApi.getById(id).pipe(
            switchMap((alert) => {
                oldName = alert.briefingName;
                alert.briefingName = name;
                const newAlert = new AlertModel(alert);
                return this.briefingApi.updateFull(newAlert.toApiModel());
            })
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Alerts.renameAlertSuccessHeading',
                    detail: this.localizationService.transform('Alerts.renameAlertSuccessMessage', { name: name })
                });
                this.refreshAlertsList();
                if (this.currentAlert?.id === id) {
                    this.currentAlert.name = name;
                    this.setCurrentAlert(this.currentAlert);
                }
            },
            error: (err) => {
                if (
                    err
                    && err.error
                    && err.error.details?.[0].code === BriefingsApiErrorEnum.nameDuplicate
                ) {
                    // Duplicate name error case
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Alerts.renameAlertErrorHeading',
                        detail: this.localizationService.transform('Alerts.saveAlertDuplicateNameErrorMessage', { name: name })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Alerts.renameAlertErrorHeading',
                        detail: this.localizationService.transform('Alerts.renameAlertErrorMessage', { oldName: oldName, name: name })
                    });
                }
                this.refreshAlertsList();
            },
        });
    }

    localizeDateBlock(block: BlockModel) {
        const styles = block.displayElement.styles as MonitoringDateStyleModel;
        styles.viewInBrowserLabel = this.localizationService.transform('Briefings.formField.date.showViewInBrowser');
    }

    setDefaultLanguage() {
        if (!this.preferences) {
            return;
        }

        this.currentAlert.blocks.forEach(block => {
            this.setLanguage(block);
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.setLanguage(x);
                    });
                });
            }
        });

        if (this.currentAlert.generalStyles) {
            if (!this.currentAlert.generalStyles.mention.locale) {
                this.currentAlert.generalStyles.mention.locale = this.languageTag as LanguageTagEnum;
            } else {
                if (this.currentAlert.generalStyles.mention.locale === 'en') {
                    this.currentAlert.generalStyles.mention.locale = LanguageTagEnum.en_US;
                }
                if (this.currentAlert.generalStyles.mention.locale === 'fr') {
                    this.currentAlert.generalStyles.mention.locale = LanguageTagEnum.fr_FR;
                }
            }
            if (!this.currentAlert.generalStyles.date.locale) {
                this.currentAlert.generalStyles.date.locale = this.languageTag as LanguageTagEnum;
            } else {
                if (this.currentAlert.generalStyles.date.locale === 'en') {
                    this.currentAlert.generalStyles.date.locale = LanguageTagEnum.en_US;
                }
                if (this.currentAlert.generalStyles.date.locale === 'fr') {
                    this.currentAlert.generalStyles.date.locale = LanguageTagEnum.fr_FR;
                }
            }
        }
    }

    setLanguage(block: BlockModel) {
        if ((block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST ||
            block.type.key === BriefingBlockTypeEnum.ALERT_MENTIONS ||
            block.type.key === BriefingBlockTypeEnum.DATE) &&
            !block.id && block.displayElement.styles
        ) {
            if (!block.displayElement.styles.locale) {
                block.displayElement.styles.locale = this.languageTag as LanguageTagEnum;
            } else {
                if (block.displayElement.styles.locale === 'en') {
                    block.displayElement.styles.locale = LanguageTagEnum.en_US;
                }
                if (block.displayElement.styles.locale === 'fr') {
                    block.displayElement.styles.locale = LanguageTagEnum.fr_FR;
                }
            }
        }
    }

    setDefaultGeneralStylesWithUserPreferences(generalStyles: any) {
        if (this.preferences && generalStyles.date) {
            const dateFormatModel = dateFormats.find(f => f.id === this.preferences.dateFormatId);
            if (dateFormatModel) {
                generalStyles.date.dateFormat = dateFormatModel.twigFormat;
            }
            const timeFormatModel = timeFormats.find(f => f.id === this.preferences.timeFormat);
            if (timeFormatModel) {
                generalStyles.date.timeFormat = timeFormatModel.momentFormat;
            }

            generalStyles.date.timezone = this.userTimezonePreference;
            generalStyles.date.showTimeZone = true;
            generalStyles.date.showTime = true;
        }
    }


    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
